import { useCallback, useEffect, useRef, useState } from 'react';
import * as THREE from 'three';
import type { SimulationRendererProps, RosterOrganism, BodyTypeSchema, GeomDef, MeshGeomDef, MeshData } from '../types/simulation';
import { FrozenTreeStore, FROZEN_RECORD_BYTES } from './frozenTrees';
import { TerrainView } from './terrainView';
import type { TerrainMessage } from './terrainView';
import { RockView } from './rocks';
import { WindField, WindMessage } from './wind';
import { GrassView } from './grass';
import { WaterView } from './waterView';
import { tideOffset } from './tide';
import { CanopyShade, CANOPY_SHADE_DECL, canopyShadeApply } from './canopyShade';
import type { CanopyLightMessage } from './canopyShade';
import type { RocksMessage } from './rocks';
import { EXPOSURE, exposureGain, installNightToneMapping, scotopicShift } from './exposure';
import { StarField } from './stars';
import type { StarsMessage } from './stars';
import { MoonView } from './moon';
import { SunGlare } from './sunGlare';

const MAX_FIXTURES = 4096;
const MAX_SITES    = 512;
const RECORD_BYTES = 43;  // 23 base + 16 quaternion + 4 geom_scale
const PARTICLE_RECORD_BYTES = 27;     // slot(4)+owner(4)+xyz(12)+radius(4)+rgb(3) — slot widened u16->u32
const PARTICLE_SUBHEADER_BYTES = 13;  // flags(1)+a(4)+b(4)+state_hash(4): same layout as the frozen section
const PARTICLE_RK_COOLDOWN  = 30;     // min frames between keyframe re-requests (retries a lost keyframe)
const MAX_FROZEN            = 2621440; // frozen (LOD unit) cells cap; MUST match ecology/frozen_field.MAX_FROZEN
const FROZEN_SUBHEADER_BYTES = 13;    // flags(1)+added_count(4)+removed_count(4)+cid_set_hash(4)
// FROZEN_RECORD_BYTES (36) lives in frozenTrees.ts next to the parser that reads it.
const FROZEN_RK_COOLDOWN     = 30;    // min frames between frozen keyframe re-requests

// CRC32 (IEEE, reflected) — byte-identical to Python's zlib.crc32, so the client can
// verify its particle state against the server's embedded hash.
const _CRC32_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    t[n] = c >>> 0;
  }
  return t;
})();
function crc32(bytes: Uint8Array): number {
  let c = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) c = (_CRC32_TABLE[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8)) >>> 0;
  return (c ^ 0xFFFFFFFF) >>> 0;
}
// crc32 of a cid as 4 little-endian bytes — matches Python zlib.crc32(struct.pack("<I", cid)),
// the unit the frozen cid-set hash is accumulated from on both ends.
const _cidBuf = new Uint8Array(4);
function crc32Cid(cid: number): number {
  _cidBuf[0] = cid & 0xFF; _cidBuf[1] = (cid >>> 8) & 0xFF;
  _cidBuf[2] = (cid >>> 16) & 0xFF; _cidBuf[3] = (cid >>> 24) & 0xFF;
  return crc32(_cidBuf);
}
// Render-cost controls (see the quality ladder and the debug overlay in the render effect).
const PIXEL_RATIO_CAP   = 1.5;    // never render more than this many pixels per CSS pixel
const FRAME_BUDGET_MS   = 10;     // max time spent decoding queued frames in one animation frame
const MAX_QUEUED_FRAMES = 600;    // 20 s of frames at 30 fps: past this the client is hopelessly behind, so drop and resync from a keyframe (must outlast first-frame shader compiles, which stall the loop for seconds)
const SLOW_FRAME_MS     = 22;     // smoothed frame time above this steps quality down (about 45 fps)
const FAST_FRAME_MS     = 17.5;   // below this (vsync-limited 60 fps) for a while steps quality back up
const PICK_INTERVAL_MS  = 100;    // crosshair hover raycast rate
const SHADOW_REFRESH_MS = 500;    // shadow map is also refreshed this often even if nothing seems to have moved
// Beach island (worlds whose terrain dips below the sea level; see waterView.ts and tide.ts).
const SEA_DRY_MARGIN    = 0.2;    // grass grows above the high-tide mark plus this many metres
const WET_DRY_TAU_S     = 20;     // seconds the wet-sand band takes to fall most of the way to a lower tide
const FOG_AIR_DENSITY   = 0.0035; // exp2 fog density in air (the scene's original value)
const FOG_UNDER_DENSITY = 0.075;  // and with the eye under the sea: about 20 m of visibility
const UNDER_FADE_M      = 0.3;    // metres of depth over which the underwater look fades in at the surface
// Quality ladder: resolution scale (on top of the pixel-ratio cap) and the share of grass drawn.
const QUALITY_LEVELS = [
  { res: 1.00, grass: 1.00 },
  { res: 0.85, grass: 1.00 },
  { res: 0.75, grass: 0.85 },
  { res: 0.65, grass: 0.70 },
  { res: 0.60, grass: 0.50 },
  { res: 0.50, grass: 0.35 },
];
const _dummy = new THREE.Object3D();

// Props: live substrate particles whose owner_id carries a flag in its high bits. They are real physics objects,
// unlike plant cells, which are not drawn.
//  - Fruit (ecology FRUIT_OWNER_FLAG, bit 31): hangs pinned in a crown while it ripens, then falls and rolls.
//    Small fruit is unripe: colour blends from green to the wire colour as the radius grows.
//  - Frond debris (FROND_OWNER_FLAG, bit 30): ONE particle for a whole frond the wind tore off a palm (the
//    frond's cells left the frozen field). Its radius is length / 12, so the frond card is drawn 12 radii long,
//    lying flat just above the ground, turning along its direction of travel and fluttering while it moves.
const MAX_FRUIT = 8192;
const MAX_FROND_DEBRIS = 2048;
const FRUIT_OWNER_FLAG = 0x80000000;
const PROP_OWNER_MIN = 0x40000000;   // owners at or above this are drawn props (fronds, then fruit)
const FRUIT_MIN_RADIUS = 0.03;       // radius at which a fruit is fully green
const FRUIT_RIPE_RADIUS = 0.10;      // radius at which it shows its wire (husk) colour
const FRUIT_GREEN: [number, number, number] = [0.38, 0.56, 0.24];
const FROND_LENGTH_PER_RADIUS = 12;
type FruitRec = [number, number, number, number, number, number, number, number];   // sim x, y, z, radius, r, g, b (0..255), owner_id
const _fruitColor = new THREE.Color();

/** A unit-length pinnate frond in the x-z plane (rachis along +x, -0.5..0.5), leaflets sweeping toward the tip and
 *  drooping a little, so it reads as a palm leaf from above. Double-sided; scale it by the frond's length. */
function makeFrondGeometry(): THREE.BufferGeometry {
  const pos: number[] = [];
  const N = 14;
  const tri = (a: number[], b: number[], c: number[]) => { pos.push(...a, ...b, ...c); };
  tri([-0.5, 0, -0.006], [0.5, 0, -0.006], [0.5, 0, 0.006]);
  tri([-0.5, 0, -0.006], [0.5, 0, 0.006], [-0.5, 0, 0.006]);
  for (let i = 0; i < N; i++) {
    const u = (i + 0.5) / N;
    const x = -0.5 + u;
    const w = 0.02 + 0.17 * Math.pow(Math.sin(Math.PI * Math.min(0.97, 0.08 + 0.9 * u)), 0.8);
    const tipX = x + 0.07;
    for (const side of [-1, 1]) {
      tri([x - 0.012, 0, 0], [x + 0.012, 0, 0], [tipX, -0.045 * w / 0.19, side * w]);
    }
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.computeVertexNormals();
  return g;
}

// Per-frond motion state for the card's heading and flutter: slot -> last position and drawn yaw / flutter.
const _frondState = new Map<number, { x: number; y: number; yaw: number; wob: number }>();

/** Rebuild the fruit and frond instances from the live prop set. Called only when a frame changed it. */
function flushProps(fruit: THREE.InstancedMesh, frond: THREE.InstancedMesh, live: Map<number, FruitRec>): void {
  let n = 0;
  let nf = 0;
  const tsec = performance.now() * 0.001;
  for (const [slot, f] of live) {
    if (f[7] >= FRUIT_OWNER_FLAG) {
      if (n >= MAX_FRUIT) continue;
      _dummy.position.set(f[0], f[2], -f[1]);                       // sim(x,y,z) -> three(x,z,-y)
      _dummy.rotation.set(0, 0, 0);
      _dummy.scale.set(f[3], f[3] * 1.15, f[3]);                    // a coconut is a little taller than wide
      _dummy.updateMatrix();
      fruit.setMatrixAt(n, _dummy.matrix);
      const t = Math.min(1, Math.max(0, (f[3] - FRUIT_MIN_RADIUS) / (FRUIT_RIPE_RADIUS - FRUIT_MIN_RADIUS)));
      _fruitColor.setRGB(
        FRUIT_GREEN[0] + (f[4] / 255 - FRUIT_GREEN[0]) * t,
        FRUIT_GREEN[1] + (f[5] / 255 - FRUIT_GREEN[1]) * t,
        FRUIT_GREEN[2] + (f[6] / 255 - FRUIT_GREEN[2]) * t,
      );
      fruit.setColorAt(n, _fruitColor);
      n++;
    } else {
      if (nf >= MAX_FROND_DEBRIS) continue;
      let st = _frondState.get(slot);
      if (!st) { st = { x: f[0], y: f[1], yaw: slot * 2.399963, wob: 0 }; _frondState.set(slot, st); }
      const dx = f[0] - st.x, dy = f[1] - st.y;
      const d = Math.hypot(dx, dy);
      if (d > 0.004) {
        // Turn the long axis toward the direction of travel (sim (dx, dy) = three (dx, -dy); about Y that is atan2(dy, dx)).
        const target = Math.atan2(dy, dx);
        let diff = target - st.yaw;
        diff = ((diff + Math.PI / 2) % Math.PI + Math.PI) % Math.PI - Math.PI / 2;     // a frond is symmetric: take the short way to either end
        st.yaw += diff * 0.15;
      }
      st.wob += (Math.min(0.45, d * 8) - st.wob) * 0.25;                               // flutter dies away as it comes to rest
      st.x = f[0]; st.y = f[1];
      const len = f[3] * FROND_LENGTH_PER_RADIUS;
      _dummy.position.set(f[0], f[2] - 0.8 * f[3], -f[1]);
      _dummy.rotation.set(st.wob * Math.sin(tsec * 9 + slot), st.yaw, st.wob * Math.cos(tsec * 7 + slot * 1.7), 'YXZ');
      _dummy.scale.set(len, len, len);
      _dummy.updateMatrix();
      frond.setMatrixAt(nf, _dummy.matrix);
      _fruitColor.setRGB(f[4] / 255, f[5] / 255, f[6] / 255);
      frond.setColorAt(nf, _fruitColor);
      nf++;
    }
  }
  if (_frondState.size > live.size + 32) {
    for (const k of Array.from(_frondState.keys())) if (!live.has(k)) _frondState.delete(k);
  }
  fruit.count = n;
  fruit.instanceMatrix.needsUpdate = true;
  if (fruit.instanceColor) fruit.instanceColor.needsUpdate = true;
  frond.count = nf;
  frond.instanceMatrix.needsUpdate = true;
  if (frond.instanceColor) frond.instanceColor.needsUpdate = true;
}
const _color = new THREE.Color();
const _skyColor = new THREE.Color();
const _underCol = new THREE.Color();                 // the sea's colour seen from inside: sky pulled toward teal
const _underTint = new THREE.Color(0.12, 0.55, 0.65);
const _fogMix = new THREE.Color();
const _glitSunK = [0, 0, 0], _glitMoonK = [0, 0, 0];   // sea glitter strengths (see waterView.ts)
const _glitMoonDir = new THREE.Vector3();
const _drawSize = new THREE.Vector2();
new THREE.Vector3();
const _fwd   = new THREE.Vector3();

// Touch controls (phones and tablets, landscape). See the touch block in the render effect.
const TOUCH_WALK_SPEED  = 8;      // full-deflection walking speed, world units / second (desktop walk is 20; there is no touch sprint)
const TOUCH_ACCEL_TAU   = 0.3;    // seconds for walking speed to ease up toward the stick (a ramp, not an instant start)
const TOUCH_STOP_TAU    = 0.1;    // seconds for it to ease down when the stick is released or eased off (short, so stopping is quick)
const TOUCH_ZOOM_MAX    = 4;      // pinch zoom limit, as magnification of the view (1 = normal)
const ZOOM_EASE_S       = 0.08;   // seconds of smoothing on the zoom, so a pinch does not step the view
const TOUCH_TAP_MS      = 250;    // a touch shorter than this, and nearly still, is a tap
const TOUCH_DOUBLE_TAP_MS = 320;  // two taps within this reset the zoom
const TOUCH_TAP_SLOP    = 12;     // px a finger may wander and still count as a tap
const TOUCH_ZONE_LEFT   = 1 / 3;  // touch zones, as fractions of the view width: left of this walks,
const TOUCH_ZONE_RIGHT  = 2 / 3;  // right of this turns, and in between is pinch zoom
const TOUCH_DEADZONE    = 0.12;   // stick deflection (0 to 1) ignored around the centre
const TOUCH_STICK_R     = 56;     // px of thumb travel for full deflection
const TOUCH_LOOK_SENS   = 0.0045; // radians of turn per pixel of drag on the right half
const GYRO_EASE_S       = 0.05;   // seconds of smoothing on tilt, so sensor noise does not shake the view

// True on devices whose primary input is a finger (the layout and controls below assume that).
function detectTouch(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(pointer: coarse)').matches && ('ontouchstart' in window || navigator.maxTouchPoints > 0);
}
const wrapPi = (a: number) => Math.atan2(Math.sin(a), Math.cos(a));
const clampPitch = (p: number) => Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, p));

// The world's day length and start hour, from the "spin" block of the config (found wherever the config
// nests it). Without it there is no clock.
interface SpinInfo { ticksPerDay: number; startHour: number }
function findSpin(obj: unknown, depth = 0): SpinInfo | null {
  if (!obj || typeof obj !== 'object' || depth > 6) return null;
  if (Array.isArray(obj) && obj.length > 64) return null;
  const o = obj as Record<string, any>;
  const s = o.spin;
  if (s && typeof s === 'object' && typeof s.ticks_per_day === 'number' && s.ticks_per_day > 0) {
    return { ticksPerDay: s.ticks_per_day, startHour: typeof s.start_hour === 'number' ? s.start_hour : 0 };
  }
  for (const k of Object.keys(o)) {
    const r = findSpin(o[k], depth + 1);
    if (r) return r;
  }
  return null;
}
// 24-hour HH:MM at a sim tick: the start hour plus the fraction of the day elapsed.
function clockText(tick: number, spin: SpinInfo): string {
  const h = (((spin.startHour + 24 * (tick % spin.ticksPerDay) / spin.ticksPerDay) % 24) + 24) % 24;
  const hh = Math.floor(h);
  const mm = Math.min(59, Math.floor((h - hh) * 60));
  return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
}

// Coordinate transform: sim(x,y,z) → Three.js(x,z,-y)  =  rotation -90° around X
// Used to convert both positions and quaternions from sim space to Three.js space.
const COORD_QUAT     = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(-90°)
const COORD_QUAT_INV = new THREE.Quaternion( Math.SQRT1_2, 0, 0, Math.SQRT1_2); // R_x(+90°)

function simQuatToThree(qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  const q = new THREE.Quaternion(qx, qy, qz, qw); // THREE uses (x,y,z,w)
  return COORD_QUAT.clone().multiply(q).multiply(COORD_QUAT_INV);
}

// Same conversion into an existing quaternion (COORD * q * COORD^-1), for the per-frame path,
// which otherwise allocated three quaternions per body segment per frame.
function simQuatToThreeInto(out: THREE.Quaternion, qw: number, qx: number, qy: number, qz: number): THREE.Quaternion {
  return out.set(qx, qy, qz, qw).premultiply(COORD_QUAT).multiply(COORD_QUAT_INV);
}

function b64ToFloat32(b64: string): Float32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Float32Array(buf.buffer);
}

function b64ToInt32(b64: string): Int32Array {
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return new Int32Array(buf.buffer);
}

// Build a Three.js BufferGeometry from a MuJoCo geom definition.
// Geometry is defined in the body's Three.js local frame (after coord transform).
// Axes after coord transform: local-x=sim-x, local-y=sim-z, local-z=-sim-y
function makeGeomGeometry(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.BufferGeometry {
  switch (geom.type) {
    case 'sphere': {
      const [s0] = geom.size;
      return new THREE.SphereGeometry(s0, 12, 8);
    }
    case 'capsule': {
      // MuJoCo capsule: axis along body-local z → Three.js local y after coord transform
      // size[0]=radius, size[1]=half-length of cylindrical part
      const [s0, s1] = geom.size;
      return new THREE.CapsuleGeometry(s0, s1 * 2, 4, 12);
    }
    case 'cylinder': {
      // Same axis mapping as capsule
      const [s0, s1] = geom.size;
      return new THREE.CylinderGeometry(s0, s0, s1 * 2, 12);
    }
    case 'box': {
      // size[0,1,2] = half-extents along sim x,y,z
      // → Three.js local: x=s0, y=s2, z=s1 (after coord transform)
      const [s0, s1, s2] = geom.size;
      return new THREE.BoxGeometry(s0 * 2, s2 * 2, s1 * 2);
    }
    case 'ellipsoid':
      return new THREE.SphereGeometry(1, 12, 8);
    case 'mesh': {
      const g = geom as MeshGeomDef;
      const meshData = meshes?.[g.mesh_key];
      if (!meshData) return new THREE.SphereGeometry(0.05, 6, 4); // fallback if schema incomplete
      const verts = b64ToFloat32(meshData.verts_b64);
      const faces  = b64ToInt32(meshData.faces_b64);
      // Transform vertices from sim Z-up (x,y,z) → Three.js Y-up (x,z,-y)
      for (let i = 0; i < verts.length; i += 3) {
        const vy = verts[i + 1], vz = verts[i + 2];
        verts[i + 1] = vz;
        verts[i + 2] = -vy;
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute('position', new THREE.BufferAttribute(verts, 3));
      geo.setIndex(new THREE.BufferAttribute(new Uint32Array(faces.buffer), 1));
      geo.computeVertexNormals();
      return geo;
    }
    default:
      return new THREE.SphereGeometry(0.05, 6, 4);
  }
}

function makeGeomMesh(geom: GeomDef, meshes?: Record<string, MeshData>): THREE.Mesh {
  const geo = makeGeomGeometry(geom, meshes);
  const [, , , a] = geom.rgba;
  const mat = new THREE.MeshStandardMaterial({
    color: new THREE.Color(0.90, 0.48, 0.15),
    roughness: 0.65,
    metalness: 0.05,
    transparent: a < 0.99,
    opacity: a,
  });
  const mesh = new THREE.Mesh(geo, mat);

  // Local position: apply coord transform to geom_pos (sim body-local)
  const [px, py, pz] = geom.pos;
  mesh.position.set(px, pz, -py);

  // Local orientation: apply coord transform to geom_quat
  const [qw, qx, qy, qz] = geom.quat;
  mesh.quaternion.copy(simQuatToThree(qw, qx, qy, qz));

  // Ellipsoid: scale the unit sphere by semi-axes
  if (geom.type === 'ellipsoid') {
    const [s0, s1, s2] = geom.size;
    mesh.scale.set(s0, s2, s1); // sim-x→three-x, sim-z→three-y, sim-y→three-z
  }

  return mesh;
}

// Build one Three.js Group for a single body segment, containing all its geom meshes.
function buildSegmentGroup(segment: { name?: string; geoms: GeomDef[] }, meshes?: Record<string, MeshData>): THREE.Group {
  const group = new THREE.Group();
  for (const geom of segment.geoms) {
    if (geom.type === 'mesh') {
      const key = (geom as MeshGeomDef).mesh_key;
      const md = meshes?.[key];
      console.log(`[sim] mesh geom key=${key} found=${!!md} verts=${md ? b64ToFloat32(md.verts_b64).length / 3 : 0}`);
    }
    group.add(makeGeomMesh(geom, meshes));
  }
  return group;
}

export default function DefaultSimRenderer({
  broadcastUrl,
  config,
  selectedEntityId,
  hoveredEntityId,
  snapToEntityId,
  onOrganismClick,
  onOrganismHover,
}: SimulationRendererProps) {
  const [broadcastOffline, setBroadcastOffline] = useState(false);
  const [webglUnavailable, setWebglUnavailable] = useState(false);
  const [isLocked, setIsLocked] = useState(false);  // pointer-lock (FPS) state, for the crosshair/hint overlay
  const panelHiddenRef = useRef(false);               // H hides the page chrome, header and left pane (the viewer's own overlays stay)
  const [isTouchDevice] = useState(detectTouch);      // finger-first device: touch controls on, keyboard and pointer lock off
  const [isPortrait, setIsPortrait] = useState(false); // touch devices only: portrait shows a "rotate to landscape" cover
  const touchHintRef = useRef<HTMLDivElement>(null);  // control hint on touch devices, hidden at the first touch
  const clockRef = useRef<HTMLSpanElement>(null);     // the 24-hour sim clock beside the tick counter
  const spinRef = useRef<SpinInfo | null>(null);      // day length and start hour from the config (null = no clock)

  const mountRef   = useRef<HTMLDivElement>(null);
  const tickDisplayRef = useRef<HTMLSpanElement>(null);
  const tickRateRef    = useRef<HTMLSpanElement>(null);
  const wsRef      = useRef<WebSocket | null>(null);
  const rosterRef  = useRef<RosterOrganism[]>([]);
  const siteRegistryRef = useRef<string[]>([]);
  // Frames wait here until the render loop decodes them, each exactly once and in order. (It used
  // to keep only the newest buffer and re-decode it on every animation frame, which repeated the
  // work 2 to 5 times at 60 to 144 Hz against a 30 fps stream, and lost a delta whenever two
  // frames landed between animation frames, which the hash audit then answered with a keyframe.)
  const frameQueueRef = useRef<{ buf: ArrayBuffer; view: DataView }[]>([]);
  const rosterByIdxRef = useRef(new Map<number, RosterOrganism>());
  const shadowDirtyRef = useRef(true);                                 // a message changed what casts shadows
  const instanceIdToOrgIdxRef = useRef(new Map<number, number>());

  // Latest sun state from the world server (via broadcast 'sun' message).
  // Angles are radians; irradiance triples are linear RGB where 1.0 is top-of-atmosphere
  // flux at the reference distance. `directRgb` is the atmosphere-filtered beam normal
  // to the ray; `skyRgb` is diffuse light on an open horizontal surface.
  const simTpsRef = useRef(100);                         // the sim's actual tick rate, from the stats message
  const sunMsgCountRef = useRef(0);                      // sun messages received, for the debug overlay
  const sunRef = useRef<{
    direction: [number, number, number];
    elevation: number;
    angularDiameter: number;
    directRgb: [number, number, number];
    skyRgb: [number, number, number];
    isDay: boolean;
    tick?: number;                                       // sim tick this state is for
    receivedAt?: number;                                 // performance.now() when the message arrived
    // For placing the catalog's stars and the moon. Absent from an older server.
    worldPos?: [number, number, number];                 // the world's universe-space position
    frame?: number[];                                    // 3x3 row-major: east, north, up in universe space
    moon?: { direction: [number, number, number]; angularDiameter: number; phase: number; rgb: [number, number, number] } | null;
  } | null>(null);

  // MJCF body schema state
  const bodySchemaRef    = useRef<Record<string, BodyTypeSchema>>({});
  // entity_id → array of Three.js Groups (one per fixture slot / body segment)
  const entityGroupsRef  = useRef<Map<string, THREE.Group[]>>(new Map());
  // fixture slot → { entityId, segIdx } for schema-rendered slots
  const slotToEntityRef  = useRef<Map<number, { entityId: string; segIdx: number }>>(new Map());
  // Reference to the scene for adding/removing groups (set during scene setup)
  const sceneRef         = useRef<THREE.Scene | null>(null);
  const fixturesRef      = useRef<THREE.InstancedMesh | null>(null); // sphere fallback
  const fruitRef         = useRef<THREE.InstancedMesh | null>(null); // fruit (live particles flagged in owner_id)
  const frondRef         = useRef<THREE.InstancedMesh | null>(null); // frond debris (live particles flagged in owner_id bit 30)
  const fruitLiveRef     = useRef<Map<number, FruitRec>>(new Map());  // particle slot -> prop record (fruit and frond debris)
  const terrainRef       = useRef<TerrainView | null>(null);         // ground heightfield (absent = flat plane)
  const terrainMsgRef    = useRef<TerrainMessage | null>(null);      // last terrain message, for a view built after it arrived
  const grassRef         = useRef<GrassView | null>(null);           // cosmetic wind-blown grass
  const canopyShadeRef   = useRef<CanopyShade | null>(null);         // plant-canopy shading map for ground and grass
  const canopyLightMsgRef = useRef<CanopyLightMessage | null>(null);  // last canopy_light message, for a view built after it arrived
  const windMsgRef       = useRef<WindMessage | null>(null);         // latest server wind message (the field may not exist yet)
  const windRef          = useRef<WindField | null>(null);           // shared wind (trees, live tips, grass)
  const waterRef         = useRef<WaterView | null>(null);           // the sea (absent or hidden when the terrain has no ground below sea level)
  const rocksRef         = useRef<RockView | null>(null);            // static boulders (absent = none)
  const rocksMsgRef      = useRef<RocksMessage | null>(null);        // last rocks message, for a view built after it arrived
  const starFieldRef     = useRef<StarField | null>(null);           // the night sky's catalog stars
  const starsMsgRef      = useRef<StarsMessage | null>(null);        // last stars message, for a view built after it arrived
  const frozenRef        = useRef<FrozenTreeStore | null>(null);     // frozen LOD-unit cells (wood segments + needle sprigs)
  // Persistent particle instance state, so sleeping cells cost nothing per frame:
  // the wire stream is keyframe + deltas, and cells not mentioned keep their instance.
  const particleHasKeyframeRef = useRef(false);                       // ignore deltas until first keyframe
  const particleKfRef = useRef<{ shadow: Map<number, number>; fruit: Map<number, FruitRec>; hash: number; next: number } | null>(null);   // keyframe being received in slices
  const particleRawRef = useRef<Map<number, number>>(new Map());      // slot -> crc32 of its record (for the running hash)
  const particleHashRef = useRef(0);                                  // running XOR of crc32(record) over our cells
  const particleCrcFrameRef = useRef(0);                              // frames since connect, for request cadence
  const lastRkFrameRef = useRef(-PARTICLE_RK_COOLDOWN);               // last keyframe-request frame
  // Frozen (LOD unit) instance state: static geometry, append/keyframe stream.
  const frozenHasKeyframeRef = useRef(false);
  const frozenHashRef = useRef(0);                                    // running XOR of crc32(cid) over our frozen set
  const frozenCrcFrameRef = useRef(0);                               // frozen frames since connect, for request cadence
  const frozenKfKeepRef = useRef<Set<number> | null>(null);          // cids seen so far in a keyframe being received in slices
  const frozenKfNextRef = useRef(0);                                  // next expected keyframe slice index
  const lastFrozenRkFrameRef = useRef(-FROZEN_RK_COOLDOWN);           // last frozen keyframe-request frame

  const selectedEntityIdRef = useRef(selectedEntityId);
  const hoveredEntityIdRef  = useRef(hoveredEntityId);
  const snapToEntityIdRef   = useRef(snapToEntityId);
  const onOrganismClickRef  = useRef(onOrganismClick);
  const onOrganismHoverRef  = useRef(onOrganismHover);
  const entityPositionsRef  = useRef<Map<string, THREE.Vector3>>(new Map());
  const raycasterTargetsRef = useRef<THREE.Mesh[]>([]);

  useEffect(() => { selectedEntityIdRef.current = selectedEntityId; }, [selectedEntityId]);
  useEffect(() => { hoveredEntityIdRef.current  = hoveredEntityId;  }, [hoveredEntityId]);
  useEffect(() => { snapToEntityIdRef.current   = snapToEntityId;   }, [snapToEntityId]);
  useEffect(() => { onOrganismClickRef.current  = onOrganismClick;  }, [onOrganismClick]);
  useEffect(() => { onOrganismHoverRef.current  = onOrganismHover;  }, [onOrganismHover]);
  useEffect(() => { spinRef.current = findSpin(config); }, [config]);

  // Landscape only on touch devices: a cover asks for rotation while the device is in portrait.
  useEffect(() => {
    if (!isTouchDevice) return;
    const mq = window.matchMedia('(orientation: portrait)');
    const update = () => setIsPortrait(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, [isTouchDevice]);

  // Rebuild entity groups whenever roster or schemas change.
  const rebuildEntityGroups = useCallback(() => {
    const scene = sceneRef.current;
    if (!scene) return;

    const roster = rosterRef.current;
    const schemas = bodySchemaRef.current;
    const entityGroups = entityGroupsRef.current;
    const slotToEntity = slotToEntityRef.current;

    // Remove groups for entities no longer in roster (or dead)
    const rosterIds = new Set(roster.filter(r => r.state !== 'dead').map(r => r.entity_id));
    for (const [entityId, groups] of entityGroups) {
      if (!rosterIds.has(entityId)) {
        for (const g of groups) scene.remove(g);
        entityGroups.delete(entityId);
        // Clean up slot mapping
        for (const [slot, info] of slotToEntity) {
          if (info.entityId === entityId) slotToEntity.delete(slot);
        }
      }
    }

    // Create groups for new entities that have a schema
    for (const org of roster.filter(r => r.state !== 'dead')) {
      if (entityGroups.has(org.entity_id)) continue;
      const bodyType = org.body_type;
      if (!bodyType || !schemas[bodyType]) continue;

      const schema = schemas[bodyType];
      const groups: THREE.Group[] = [];
      for (let segIdx = 0; segIdx < schema.bodies.length && segIdx < org.fixture_slots.length; segIdx++) {
        const group = buildSegmentGroup(schema.bodies[segIdx], schema.meshes);
        group.traverse(obj => { if (obj instanceof THREE.Mesh) obj.userData.entityId = org.entity_id; });
        scene.add(group);
        groups.push(group);
        slotToEntity.set(org.fixture_slots[segIdx], { entityId: org.entity_id, segIdx });
      }
      if (groups.length > 0) entityGroups.set(org.entity_id, groups);
    }

    // Rebuild flat mesh list for raycasting
    const targets: THREE.Mesh[] = [];
    for (const groups of entityGroups.values()) {
      for (const g of groups) {
        g.traverse(obj => { if (obj instanceof THREE.Mesh) targets.push(obj); });
      }
    }
    raycasterTargetsRef.current = targets;
  }, []);

  useEffect(() => {
    const scene = sceneRef.current;
    const foodItems = config?.world?.static_objects?.food;
    if (!scene || !foodItems?.length) return;

    const geo = new THREE.SphereGeometry(0.4, 8, 6);
    const mat = new THREE.MeshStandardMaterial({
      color: 0x5aaa3a,
      emissive: new THREE.Color(0.2, 0.5, 0.05),
      emissiveIntensity: 1.2,
      roughness: 0.7,
      metalness: 0.0,
    });
    const meshes = foodItems.map(item => {
      const [fx, fy, fz = 0] = item.position;
      const mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(fx, fz, -fy); // sim(x,y,z) → three(x,z,-y)
      scene.add(mesh);
      return mesh;
    });

    return () => {
      for (const m of meshes) scene.remove(m);
      geo.dispose();
      mat.dispose();
    };
  }, [config]);

  const connectBroadcast = useCallback(() => {
    wsRef.current?.close();
    // Fresh connection: discard particle state and wait for the server's keyframe.
    particleHasKeyframeRef.current = false;
    particleKfRef.current = null;
    particleRawRef.current.clear();
    particleHashRef.current = 0;
    particleCrcFrameRef.current = 0;
    fruitLiveRef.current.clear();
    if (fruitRef.current) { fruitRef.current.count = 0; }
    if (frondRef.current) { frondRef.current.count = 0; }
    _frondState.clear();
    lastRkFrameRef.current = -PARTICLE_RK_COOLDOWN;
    frozenHasKeyframeRef.current = false;
    frozenHashRef.current = 0;
    frozenCrcFrameRef.current = 0;
    frozenKfKeepRef.current = null;
    frozenKfNextRef.current = 0;
    lastFrozenRkFrameRef.current = -FROZEN_RK_COOLDOWN;
    frozenRef.current?.clear();
    frameQueueRef.current.length = 0;
    const ws = new WebSocket(broadcastUrl);
    ws.binaryType = 'arraybuffer';
    wsRef.current = ws;

    ws.onmessage = (evt) => {
      if (typeof evt.data === 'string') {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.type === 'roster') {
            rosterRef.current = msg.organisms ?? [];
            rosterByIdxRef.current = new Map(rosterRef.current.map(r => [r.idx, r]));
            shadowDirtyRef.current = true;
            console.log('[sim] roster', msg.organisms);
            rebuildEntityGroups();
          } else if (msg.type === 'body_schema') {
            bodySchemaRef.current = msg.schemas ?? {};
            shadowDirtyRef.current = true;
            const schemas = msg.schemas ?? {};
            for (const [bt, s] of Object.entries(schemas) as [string, any][]) {
              const meshCount = Object.keys(s.meshes ?? {}).length;
              const geomCounts = (s.bodies ?? []).map((b: any) => `${b.name}:${(b.geoms ?? []).length}geoms`);
              console.log(`[sim] body_schema ${bt}: ${meshCount} meshes, bodies=[${geomCounts.join(', ')}]`);
            }
            rebuildEntityGroups();
          } else if (msg.type === 'site_registry') {
            siteRegistryRef.current = msg.sites ?? [];
          } else if (msg.type === 'terrain') {
            terrainMsgRef.current = msg as TerrainMessage;
            shadowDirtyRef.current = true;
            terrainRef.current?.load(msg as TerrainMessage);
            grassRef.current?.setTerrain(terrainRef.current?.field() ?? null);
            {
              const tv = terrainRef.current;
              grassRef.current?.setDryLine(tv?.hasSea ? tv.seaLevel + tv.tideAmplitude + SEA_DRY_MARGIN : -1e6);
              waterRef.current?.setTerrain(tv?.field() ?? null);
            }
          } else if (msg.type === 'wind') {
            windMsgRef.current = msg as WindMessage;
            windRef.current?.applyServer(msg as WindMessage);
          } else if (msg.type === 'canopy_light') {
            canopyLightMsgRef.current = msg as CanopyLightMessage;
            void canopyShadeRef.current?.load(msg as CanopyLightMessage);
          } else if (msg.type === 'rocks') {
            rocksMsgRef.current = msg as RocksMessage;
            shadowDirtyRef.current = true;
            rocksRef.current?.load(msg as RocksMessage);
          } else if (msg.type === 'stars') {
            starsMsgRef.current = msg as StarsMessage;
            void starFieldRef.current?.load(msg as StarsMessage);
          } else if (msg.type === 'sun') {
            sunMsgCountRef.current++;
            sunRef.current = {
              direction:       msg.direction,
              elevation:       msg.elevation,
              angularDiameter: msg.angular_diameter,
              directRgb:       msg.direct_rgb,
              skyRgb:          msg.sky_rgb,
              isDay:           msg.is_day,
              tick:            typeof msg.tick === 'number' ? msg.tick : undefined,
              receivedAt:      performance.now(),
              worldPos:        msg.world_pos,
              frame:           Array.isArray(msg.frame) ? ([] as number[]).concat(...(msg.frame as number[][])) : undefined,
              moon:            msg.moon ? {
                direction: msg.moon.direction, angularDiameter: msg.moon.angular_diameter,
                phase: msg.moon.phase, rgb: msg.moon.rgb,
              } : null,
            };
          } else if (msg.type === 'stats') {
            const hz = msg.actual_tick_rate as number;
            simTpsRef.current = hz > 0 ? hz : 0;
            if (tickRateRef.current) tickRateRef.current.textContent = hz > 0 ? hz.toFixed(1) : '—';
          }
        } catch { /* ignore malformed */ }
        return;
      }
      const buf = evt.data as ArrayBuffer;
      if (buf.byteLength < 18) return;
      const q = frameQueueRef.current;
      q.push({ buf, view: new DataView(buf) });
      if (q.length > MAX_QUEUED_FRAMES) {
        // Hopelessly behind (a long stall): keep only the newest frame and drop the keyframe state,
        // which makes the decoder ask the server for fresh keyframes instead of applying deltas
        // that no longer chain.
        q.splice(0, q.length - 1);
        particleHasKeyframeRef.current = false;
        particleKfRef.current = null;     // a keyframe in progress lost slices; the decoders ask for new ones
        frozenHasKeyframeRef.current = false;
        frozenKfKeepRef.current = null;   // a keyframe in progress lost slices; the decoder asks for a new one
      }
    };

    ws.onerror = () => setBroadcastOffline(true);
    ws.onclose = () => { wsRef.current = null; };
  }, [broadcastUrl, rebuildEntityGroups]);

  useEffect(() => {
    setBroadcastOffline(false);
    connectBroadcast();
    return () => { wsRef.current?.close(); wsRef.current = null; };
  }, [connectBroadcast]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    // ── Renderer ────────────────────────────────────────────────────────────
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true });
    } catch {
      setWebglUnavailable(true);
      return;
    }
    // Pixel ratio is capped (a 2x or 3x display would shade 4 to 9 times the pixels of a 1x one, through
    // a heavy ground shader) and then scaled by the quality ladder below.
    // The HI-RES button (touch) swaps basePixelRatio for the screen's native ratio and back.
    const nativePixelRatio = window.devicePixelRatio || 1;
    let basePixelRatio = Math.min(nativePixelRatio, PIXEL_RATIO_CAP);
    renderer.setPixelRatio(basePixelRatio);
    renderer.setSize(mount.clientWidth, mount.clientHeight);
    renderer.domElement.style.touchAction = 'none';
    // ACES by day; at night a toe-free curve, so the ground the sky lights is not clipped to black.
    renderer.toneMapping = installNightToneMapping() ? THREE.CustomToneMapping : THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.3;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    // The shadow map is refreshed on demand (see the frame loop), not redrawn every frame: hills and
    // boulders are static and only the quadruped and the sun move.
    renderer.shadowMap.autoUpdate = false;
    renderer.shadowMap.needsUpdate = true;
    renderer.info.autoReset = false;       // reset once per frame, just before the main render, so the counts are the scene's
    mount.appendChild(renderer.domElement);

    // World extents (for ground plane + shadow frustum). Fall back to 128 if config absent.
    const dims = (config as any)?.universe?.grid_dimensions as number[] | undefined;
    const worldW = dims?.[0] ?? 128;
    const worldH = dims?.[1] ?? 128;
    const worldMax = Math.max(worldW, worldH);
    const cx = worldW / 2;
    const cz = -worldH / 2;  // sim y maps to three -z

    // ── Scene ───────────────────────────────────────────────────────────────
    const scene = new THREE.Scene();
    // Sky and fog colours are derived each frame from the sun message's skylight (see
    // the sun block in animate()); these are only the pre-first-message defaults.
    scene.background = new THREE.Color(0x05070f);
    scene.fog = new THREE.FogExp2(0x05070f, 0.0035);
    sceneRef.current = scene;

    // ── Camera ──────────────────────────────────────────────────────────────
    // Coordinate mapping: sim(x, y, z) → Three.js(x, z, -y). FPS camera: it stands
    // on the ground plane at a fixed eye height and is driven by pointer-lock look +
    // WASD walk (below). Starts near one edge of the patch looking across it.
    const camera = new THREE.PerspectiveCamera(75, mount.clientWidth / mount.clientHeight, 0.1, worldMax * 20);

    // ── FPS controls: pointer-lock mouselook + WASD walk ─────────────────────
    // Tunables.
    const EYE_HEIGHT       = 2.0;    // camera height above the ground plane (three-y)
    const MOVE_SPEED       = 20;     // walk speed, world units / second
    const SPRINT_MULT      = 2.5;    // hold Shift to sprint
    const LOOK_SENSITIVITY = 0.002;  // radians of view rotation per pixel of mouse motion
    const PICK_PX          = 30;     // screen-space fallback pick radius for center selection
    const isTouch          = detectTouch();

    camera.position.set(cx, EYE_HEIGHT, 8);  // three-z ≈ 8 is near the sim-y=0 edge
    camera.rotation.order = 'YXZ';
    let yaw   = 0;    // look along -z, into the patch
    let pitch = 0;    // level
    camera.rotation.set(pitch, yaw, 0, 'YXZ');

    let locked = false;
    const keys = new Set<string>();
    let prevHoveredFromCanvas: string | undefined;
    const canvas = renderer.domElement;

    // ── Lighting — sun-driven (day/night, shadows) ───────────────────────────
    // Light levels come from the world server in physical-ish linear units spanning
    // ~9 decades (noon ~1, a full moon ~1e-6, a moonless night ~1e-9). A single exposure gain, a pure
    // function of the current luminance (exposure.ts), scales tone-mapping exposure and the sky colour,
    // compressing that range into something displayable. The gain only partly compensates, so night
    // still reads darker than day. The constants that set how dark a moonless night gets live in
    // exposure.ts (EXPOSURE).
    const BASE_EXPOSURE = 1.3;    // tone-mapping exposure at full daylight (gain = 1)
    const SKY_EXPOSURE  = 6.0;    // skylight -> sky colour: 1 - exp(-SKY_EXPOSURE * gain * sky)
    const LIGHT_GAIN    = 2.4;    // directional light intensity per unit of direct_rgb
    const AMBIENT_GAIN  = 4.8;    // ambient light intensity per unit of sky_rgb
    let exposure = 1;             // the current gain, for the debug overlay
    // Whether the canvas encodes its output as sRGB itself, which the star and moon shaders must mimic
    // because they write display values directly (they bypass tone mapping, like the sun disc).
    const srgbOut = ((renderer as any).outputColorSpace ?? '') === (THREE as any).SRGBColorSpace
      || (renderer as any).outputEncoding === (THREE as any).sRGBEncoding;

    // Ambient stands in for skylight; colour and intensity come from the sun message.
    const ambient = new THREE.AmbientLight(0xffffff, 0);
    scene.add(ambient);

    // The sun's light: a directional light whose direction is updated each frame from
    // the sun message. Colour is the atmosphere-filtered beam. Casts shadows onto the
    // ground. Intensity is set to 0 (not hidden) at night so the light count, and with
    // it every material's shader, stays fixed.
    const sunLight = new THREE.DirectionalLight(0xffffff, 0);
    sunLight.position.set(cx, worldMax, cz);
    sunLight.target.position.set(cx, 0, cz);
    sunLight.castShadow = true;
    sunLight.shadow.mapSize.set(2048, 2048);
    const shadowCam = sunLight.shadow.camera as THREE.OrthographicCamera;
    shadowCam.left = -worldMax; shadowCam.right = worldMax;
    shadowCam.top = worldMax;   shadowCam.bottom = -worldMax;
    // The depth range is what a shadow bias is measured against: bias is a fraction of (far - near), so
    // the old 1..4 x worldMax range (about 511 m) turned a bias of -0.0008 into a 0.4 m offset along the
    // light, which leaves a lit band between a boulder and its shadow and makes it look like it hovers.
    // The range is now just the patch (its half-diagonal plus headroom for tall trees) around the sun's
    // distance (LIGHT_DIST = 2 x worldMax, see the sun block), about 260 m, and the bias is a few
    // centimetres, with a normal offset to keep the gently sloped ground from striping.
    const SUN_SHADOW_DIST = worldMax * 2;
    const SUN_SHADOW_R = Math.hypot(worldW, worldH) / 2 + 40;
    shadowCam.near = Math.max(1, SUN_SHADOW_DIST - SUN_SHADOW_R);
    shadowCam.far = SUN_SHADOW_DIST + SUN_SHADOW_R;
    sunLight.shadow.bias = -0.0002;
    sunLight.shadow.normalBias = 0.05;
    scene.add(sunLight);
    scene.add(sunLight.target);

    // Shadow refresh. Current three lets each light decide whether its own shadow map is redrawn, so
    // the sun's (big, static between sun and body moves) can stay on demand while the flashlight's
    // redraws every frame it is lit. Older three has only the global switch, where a lit flashlight
    // means the sun's map is redrawn every frame too (the original behaviour).
    const perLightShadows = 'autoUpdate' in (sunLight.shadow as any) && 'needsUpdate' in (sunLight.shadow as any);
    if (perLightShadows) {
      renderer.shadowMap.autoUpdate = true;
      (sunLight.shadow as any).autoUpdate = false;
      (sunLight.shadow as any).needsUpdate = true;
    }

    // ── Flashlight (F) ───────────────────────────────────────────────────────
    // A spotlight held just below and right of the eye, aimed at the point under the crosshair. Purely
    // visual: it is drawn by the client and the simulation, and the quadruped's sensors, do not see it.
    // It is a real light in the world's units, so the eye-adaptation model decides how bright it looks:
    // FLASH_GLOW is how bright it reads at full night adaptation (raise for a stronger beam), and in
    // daylight the same beam is invisible, as it should be. It does not feed adaptation itself.
    // Lives in the scene only while on (a spot light with a shadow adds per-pixel cost to every lit
    // material, and switching it in the first time recompiles those materials once: a short hitch).
    const FLASH_GLOW      = 60;       // display brightness of the beam at night, per metre squared, about
    const FLASH_ANGLE     = 0.30;     // cone half-angle, radians (about 17 degrees)
    const FLASH_PENUMBRA  = 0.65;     // fraction of the cone that is soft edge
    const FLASH_RANGE     = 40;       // shadow frustum depth, metres
    const FLASH_FADE_S    = 0.15;     // seconds to fade in and out
    const FLASH_INTENSITY = FLASH_GLOW / (BASE_EXPOSURE * EXPOSURE.A_MAX);
    const flash = new THREE.SpotLight(0xfff0d8, 0, 0, FLASH_ANGLE, FLASH_PENUMBRA, 2);
    flash.castShadow = true;
    flash.shadow.mapSize.set(1024, 1024);
    flash.shadow.camera.near = 0.1;
    flash.shadow.camera.far = FLASH_RANGE;
    flash.shadow.bias = -0.0004;
    flash.shadow.normalBias = 0.02;
    let flashWanted = false, flashLevel = 0, flashInScene = false;
    let sunBeamLum = 1;                 // luminance of the sun's direct beam on the ground, set by the sun block
    const _flashFwd = new THREE.Vector3();

    // ── Visible sun body ─────────────────────────────────────────────────────
    // The sun is drawn at infinity. The simulation models it with parallel rays (one
    // direction for the whole patch, matching the shadows and the light field), so a
    // finite-distance sun would show parallax the physics does not have. Its apparent
    // size comes from the server's angular diameter, which is where orbit radius shows
    // up. The orb is locked to the camera position each frame at SKY_R, well inside the
    // far plane and outside the patch. It is unlit and exempt from tone mapping: its
    // colour is the beam's hue (normalised), not a radiometric value.
    const SKY_R = worldMax * 10;
    // Drawn size of the sun disc relative to its true angular size. A 75 degree view on a monitor
    // shrinks the sky, so the true half degree reads as a few pixels; 1 is exact.
    const SUN_SIZE_SCALE = 2.0;
    const sunOrbGeo = new THREE.SphereGeometry(1, 32, 16);
    const sunOrbMat = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: false, toneMapped: false });
    const sunOrb = new THREE.Mesh(sunOrbGeo, sunOrbMat);
    sunOrb.frustumCulled = false;
    scene.add(sunOrb);
    // A soft glow around the disc so it reads as bright rather than a flat coin (see sunGlare.ts).
    const sunGlare = new SunGlare();
    scene.add(sunGlare.mesh);
    const glareColor = new THREE.Color(1, 1, 1);
    // Invisible horizon. Writes depth but no colour, so anything beyond the world (the
    // sun, at SKY_R) is clipped where the ground would cut it off. Without it the disc
    // sinks past the edge of the finite ground plane and stays visible below the
    // horizon. Sits below the lowest terrain and follows the camera in x/z.
    const HORIZON_Y = -6;
    const horizonGeo = new THREE.CircleGeometry(SKY_R * 1.5, 48);
    const horizonMat = new THREE.MeshBasicMaterial({ colorWrite: false, depthWrite: true });
    const horizonMesh = new THREE.Mesh(horizonGeo, horizonMat);
    horizonMesh.rotation.x = -Math.PI / 2;
    horizonMesh.renderOrder = -10;
    horizonMesh.frustumCulled = false;
    scene.add(horizonMesh);
    const _white = new THREE.Color(1, 1, 1);
    const lastDisc = new THREE.Color(1, 0.98, 0.92);   // held while the beam is 0 (limb still visible)

    // ── Stars and moon ───────────────────────────────────────────────────────
    // Real catalog stars, placed from the world's universe position and frame (see stars.ts), and the
    // moon as a lit sphere. Both are camera-locked at the sky radius like the sun, and both are hidden
    // below the horizon by the horizon disc above. The catalog arrives once per connection.
    const wind = new WindField();            // shared by stars' shimmer, grass, trees and live tips; advanced in the frame loop
    windRef.current = wind;
    if (windMsgRef.current) wind.applyServer(windMsgRef.current);   // a message that arrived before the scene existed
    const starField = new StarField(SKY_R * 0.98, wind);
    scene.add(starField.object);
    starFieldRef.current = starField;
    if (starsMsgRef.current) void starField.load(starsMsgRef.current);
    const moonView = new MoonView(SKY_R);
    scene.add(moonView.disc);
    scene.add(moonView.halo);
    const _sunDirThree = new THREE.Vector3();

    // ── Ground plane (receives shadows) ──────────────────────────────────────
    const groundGeo = new THREE.PlaneGeometry(worldMax * 3, worldMax * 3);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x3a3226, roughness: 0.95, metalness: 0.0,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;  // lie flat on the XZ plane at y=0 (sim z=0)
    ground.position.set(cx, 0, cz);
    ground.receiveShadow = true;
    scene.add(ground);

    // ── Floor grid ───────────────────────────────────────────────────────────
    const gridHelper = new THREE.GridHelper(512, 64, 0x2a2a2a, 0x1a1a1a);
    gridHelper.position.set(64, 0.02, -64);
    scene.add(gridHelper);

    // ── Terrain: hills replace the flat plane when the server sends a heightfield ──
    const canopyShade = new CanopyShade(worldW, worldH);
    canopyShadeRef.current = canopyShade;
    if (canopyLightMsgRef.current) void canopyShade.load(canopyLightMsgRef.current);
    // The flat fallback plane (a world with no terrain, and the apron beyond the walls) gets the
    // same canopy shading as the terrain surface.
    groundMat.onBeforeCompile = (sh) => {
      Object.assign(sh.uniforms, canopyShade.uniforms);
      sh.vertexShader = sh.vertexShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCanopyPos;')
        .replace('#include <begin_vertex>', '#include <begin_vertex>\nvCanopyPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');
      sh.fragmentShader = sh.fragmentShader
        .replace('#include <common>', '#include <common>\nvarying vec3 vCanopyPos;\n' + CANOPY_SHADE_DECL)
        .replace('#include <lights_fragment_end>', '#include <lights_fragment_end>\n' + canopyShadeApply('vCanopyPos'));
    };
    const terrain = new TerrainView(scene, ground, gridHelper, canopyShade);
    terrainRef.current = terrain;
    if (terrainMsgRef.current) terrain.load(terrainMsgRef.current);

    // ── Grass: cosmetic, wind-blown, follows the terrain surface ──
    const grass = new GrassView(scene, wind, { bounds: [worldW, worldH], shade: canopyShade });
    grass.setTerrain(terrain.field());
    grass.setDryLine(terrain.hasSea ? terrain.seaLevel + terrain.tideAmplitude + SEA_DRY_MARGIN : -1e6);
    grassRef.current = grass;

    // ── Sea: drawn over the seabed when the terrain dips below sea level (beach island) ──
    const water = new WaterView(scene);
    water.setTerrain(terrain.field());
    waterRef.current = water;
    let wetY = 0;                  // three-y of the wet-sand level: follows the tide up at once, down slowly
    let seaPlaced = false;         // the camera is put on the beach once, when the island's terrain first arrives

    // ── Rocks: static boulders, placed by the server on the same terrain the physics uses ──
    const rocks = new RockView(scene);
    rocksRef.current = rocks;
    if (rocksMsgRef.current) rocks.load(rocksMsgRef.current);

    // ── Sphere fallback InstancedMesh (for fixtures without body schema) ─────
    const fixGeo = new THREE.SphereGeometry(1, 8, 6);
    // NOTE: no `vertexColors: true`. Per-instance colour comes from instanceColor
    // (setColorAt), which the shader applies via USE_INSTANCING_COLOR on its own.
    // Enabling vertexColors here turns on USE_COLOR, which multiplies by the
    // geometry's per-vertex `color` attribute — absent on this sphere, so it reads
    // as (0,0,0) and zeroes every instance to black.
    const fixMat = new THREE.MeshStandardMaterial({
      color: new THREE.Color(1.0, 1.0, 1.0),
      roughness: 0.65,
      metalness: 0.05,
    });
    const fixtures = new THREE.InstancedMesh(fixGeo, fixMat, MAX_FIXTURES);
    fixtures.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fixtures.frustumCulled = false;
    fixtures.castShadow = true;
    fixtures.count = 0;
    scene.add(fixtures);
    fixturesRef.current = fixtures;

    // ── Managed-field particles (live plant cells) ───────────────────────────
    // Not drawn. Growing tips are the short-lived frontier of a tree and add nothing to the
    // rendered view; a cell appears once it freezes (see the frozen section below). The wire
    // records are still parsed so the running hash audit keeps the stream honest.
    // The exception is props: particles flagged in owner_id are drawn as instanced fruit spheres (bit 31) and
    // frond cards (bit 30), see flushProps; the instance sets are rebuilt only on frames that change them.
    const fruitMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(1, 1, 1), roughness: 0.8, metalness: 0.0 });
    const fruitMesh = new THREE.InstancedMesh(new THREE.SphereGeometry(1, 10, 8), fruitMat, MAX_FRUIT);
    fruitMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    fruitMesh.frustumCulled = false;
    fruitMesh.castShadow = true;
    fruitMesh.count = 0;
    scene.add(fruitMesh);
    fruitRef.current = fruitMesh;
    const frondMat = new THREE.MeshStandardMaterial({ color: new THREE.Color(1, 1, 1), roughness: 0.9, metalness: 0.0, side: THREE.DoubleSide });
    const frondMesh = new THREE.InstancedMesh(makeFrondGeometry(), frondMat, MAX_FROND_DEBRIS);
    frondMesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    frondMesh.frustumCulled = false;
    frondMesh.castShadow = false;
    frondMesh.count = 0;
    scene.add(frondMesh);
    frondRef.current = frondMesh;

    // ── Frozen (LOD unit) cells: tapered wood segments + needle sprigs ────────
    // Demoted plant structure is static, so instance data is written once when a record
    // arrives and never touched per frame (only a per-tree girth texel eases with age).
    // See frozenTrees.ts. No shadow casting, same reasoning as the particles above.
    // Trees sway in the shared wind field created with the grass above.
    const frozen = new FrozenTreeStore(scene, MAX_FROZEN, wind, canopyShade);
    frozenRef.current = frozen;

    // ── Site points ──────────────────────────────────────────────────────────
    const sitePosArr = new Float32Array(MAX_SITES * 3);
    const siteColArr = new Float32Array(MAX_SITES * 3);
    const siteGeo = new THREE.BufferGeometry();
    siteGeo.setAttribute('position', new THREE.BufferAttribute(sitePosArr, 3));
    siteGeo.setAttribute('color',    new THREE.BufferAttribute(siteColArr, 3));
    siteGeo.setDrawRange(0, 0);
    scene.add(new THREE.Points(siteGeo, new THREE.PointsMaterial({
      size: 5, sizeAttenuation: false, vertexColors: true,
    })));

    // ── Quality ladder, debug overlay and toggles ────────────────────────────
    // A frame-time controller (in the frame loop) steps down the ladder when frames run slow and
    // back up when they have been fast for a while; each step lowers the render resolution and
    // the share of grass drawn. The overlay (backtick, or ?simdebug in the URL) shows what the
    // frame costs and lets you switch parts off to find the expensive one: 1 grass, 2 trees,
    // 3 sun shadows, 4 boulders, 5 auto quality on/off, [ and ] step quality by hand.
    const show = { grass: true, trees: true, shadows: true, rocks: true };
    // Full quality and fixed by default (level 0, controller off). ?quality=0..5 pins a lower level and
    // ?quality=auto switches the adaptive controller on; [ ] and 5 (overlay open) still work at runtime.
    let autoQuality = false;
    let qLevel = 0;
    const applyQuality = () => {
      const lv = QUALITY_LEVELS[qLevel];
      renderer.setPixelRatio(basePixelRatio * lv.res);
      grassRef.current?.setQuality(lv.grass);
    };
    // ?quality=0..5 forces that level from the start (0 = full resolution, 5 = half); ?quality=auto
    // hands control to the adaptive controller; no parameter means full quality, fixed.
    const qParam = new URLSearchParams(window.location.search).get('quality');
    if (qParam === 'auto') {
      autoQuality = true;
    } else if (qParam !== null) {
      const n = parseInt(qParam, 10);
      if (Number.isFinite(n)) { qLevel = Math.min(QUALITY_LEVELS.length - 1, Math.max(0, n)); applyQuality(); }
    }
    let dbgOn = new URLSearchParams(window.location.search).has('simdebug');
    const dbg = document.createElement('div');
    dbg.style.cssText = 'position:absolute;top:30px;left:10px;padding:6px 8px;background:rgba(0,0,0,0.6);' +
      'color:#9fe8c8;font:11px/1.45 "Space Mono",monospace;white-space:pre;pointer-events:none;z-index:5;';
    dbg.style.display = dbgOn ? 'block' : 'none';
    mount.appendChild(dbg);

    // ── Input: raycasting + FPS control handlers ─────────────────────────────
    const raycaster = new THREE.Raycaster();
    let prevHighlightedId: string | undefined;

    // Movement scratch vectors + fixed screen-center pick point.
    const _right  = new THREE.Vector3();
    const _move   = new THREE.Vector3();
    const _center = new THREE.Vector2(0, 0);
    const clock   = new THREE.Clock();

    const findClosestEntityOnScreen = (px: number, py: number, rect: DOMRect, threshold: number): string | undefined => {
      let best: string | undefined;
      let bestDist = threshold;
      for (const [eid, pos] of entityPositionsRef.current) {
        _pickVec.copy(pos).project(camera);
        if (_pickVec.z > 1) continue; // behind camera
        const sx = (_pickVec.x + 1) / 2 * rect.width;
        const sy = (-_pickVec.y + 1) / 2 * rect.height;
        const d = Math.hypot(sx - px, sy - py);
        if (d < bestDist) { bestDist = d; best = eid; }
      }
      return best;
    };
    const _pickVec = new THREE.Vector3();

    // Raycast from the screen center (the crosshair) and return the entity under it,
    // if any. Same three-tier resolution as the old mouse pick: schema meshes, then
    // the sphere-fallback InstancedMesh, then a screen-space nearest fallback.
    const pickFromCenter = (): string | undefined => {
      raycaster.setFromCamera(_center, camera);
      const schemaTargets = raycasterTargetsRef.current;
      if (schemaTargets.length > 0) {
        const hits = raycaster.intersectObjects(schemaTargets, false);
        if (hits.length > 0) return (hits[0].object as THREE.Mesh).userData.entityId;
      }
      const fhits = raycaster.intersectObject(fixtures);
      const instanceId = fhits.length > 0 ? fhits[0].instanceId : undefined;
      const orgIdx = instanceId !== undefined ? (instanceIdToOrgIdxRef.current.get(instanceId) ?? -1) : -1;
      if (orgIdx !== -1) return rosterRef.current.find(r => r.idx === orgIdx)?.entity_id;
      const rect = canvas.getBoundingClientRect();
      return findClosestEntityOnScreen(rect.width / 2, rect.height / 2, rect, PICK_PX);
    };

    const selectFromCenter = () => {
      const id = pickFromCenter();
      onOrganismClickRef.current?.(
        id ? (id === selectedEntityIdRef.current ? undefined : id) : undefined
      );
    };

    // A click on the canvas engages pointer lock; once locked, a click selects
    // whatever is under the crosshair. The lock-engaging click fires while still
    // unlocked, so it never doubles as a selection.
    const onCanvasMouseDown = () => {
      if (isTouch) return;   // fingers drive the touch handlers below; pointer lock is for mouse and keyboard
      if (!locked) { canvas.requestPointerLock(); return; }
      selectFromCenter();
    };

    const onPointerLockChange = () => {
      locked = document.pointerLockElement === canvas;
      setIsLocked(locked);
      if (!locked) keys.clear();  // drop held keys so movement stops cleanly on Esc
    };

    const onMouseMove = (e: MouseEvent) => {
      if (!locked) return;
      yaw   -= e.movementX * LOOK_SENSITIVITY;
      pitch -= e.movementY * LOOK_SENSITIVITY;
      pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, pitch));
      camera.rotation.set(pitch, yaw, 0, 'YXZ');
    };

    // Hide or show the page chrome (header and left pane) so the viewer fills the window. Shared by the
    // H key and the touch fullscreen button.
    const setUiHidden = (hidden: boolean) => {
      panelHiddenRef.current = hidden;
      if (hidden) document.documentElement.setAttribute('data-sim-ui-hidden', ''); else document.documentElement.removeAttribute('data-sim-ui-hidden');
      window.dispatchEvent(new CustomEvent('sim-ui-hidden', { detail: { hidden } }));
    };

    const onKeyDown = (e: KeyboardEvent) => {
      keys.add(e.code);
      if (e.repeat) return;
      if (e.code === 'KeyH' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        // H hides or shows the page chrome (header and left pane) so the viewer fills the window. The
        // viewer's own overlays (crosshair, hints, tick readout, debug panel) are untouched. The page
        // hides through an attribute on <html> (a class on <body> would be wiped by the theme switcher,
        // which assigns body.className); the same state also goes out as a window event.
        const t = e.target as HTMLElement | null;
        if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable)) return;
        setUiHidden(!panelHiddenRef.current);
        return;
      }
      if (e.code === 'Backquote') { dbgOn = !dbgOn; dbg.style.display = dbgOn ? 'block' : 'none'; return; }
      // [ and ] step the quality by hand at any time (overlay or not) and switch auto quality off, so
      // the level you pick is the level you get. 5 (overlay open) turns auto back on.
      if (e.code === 'BracketLeft' || e.code === 'BracketRight') {
        autoQuality = false;
        qLevel = e.code === 'BracketLeft' ? Math.min(QUALITY_LEVELS.length - 1, qLevel + 1) : Math.max(0, qLevel - 1);
        applyQuality(); fpsClock = 0; calmFor = 0; probing = false;
        return;
      }
      if (e.code === 'KeyF') { flashWanted = !flashWanted; return; }
      if (!dbgOn) return;
      switch (e.code) {
        case 'Digit1': show.grass = !show.grass; grassRef.current?.setEnabled(show.grass); break;
        case 'Digit2': show.trees = !show.trees; frozenRef.current?.setEnabled(show.trees); break;
        case 'Digit3': show.shadows = !show.shadows; sunLight.castShadow = show.shadows; break;   // recompiles lit materials once
        case 'Digit4': show.rocks = !show.rocks; rocksRef.current?.setEnabled(show.rocks); break;
        case 'Digit5': autoQuality = !autoQuality; break;
      }
    };
    const onKeyUp   = (e: KeyboardEvent) => { keys.delete(e.code); };

    canvas.addEventListener('mousedown', onCanvasMouseDown);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    document.addEventListener('mousemove', onMouseMove);
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup',   onKeyUp);

    // ── Touch: floating walk stick, drag to turn, tilt to aim, fullscreen ─────
    // Landscape, three zones by where a finger lands. A touch in the left third plants a floating stick
    // under that thumb. It is analog: how far the thumb is pushed sets the walking speed, up to
    // TOUCH_WALK_SPEED, and there is no sprint. A touch in the right third turns the view as it drags, and
    // two fingers in the middle third pinch to zoom (a double tap there resets the zoom).
    // Tilt (when switched on with its button) adds the phone's own rotation on top of the dragged angles,
    // measured from the pose it had when switched on, so the thumb does the big turns and the wrist the
    // fine aim. The frame loop reads the state below; the handlers only write it.
    let stickVX = 0, stickVY = 0;          // stick deflection, -1 to 1 (x right, y forward)
    let walkR = 0, walkF = 0;              // eased walking velocity, world units / second (right, forward)
    let gyroYawT = 0, gyroPitchT = 0;      // tilt offsets the sensors ask for
    let gyroYaw = 0, gyroPitch = 0;        // tilt offsets drawn (eased)
    let zoomT = 1, zoom = 1;               // pinch zoom: the target and the drawn (eased) magnification, 1 to TOUCH_ZOOM_MAX
    const baseFov = camera.fov;            // the unzoomed vertical field of view, degrees
    const touchUiCleanup: Array<() => void> = [];

    if (isTouch) {
      canvas.style.setProperty('-webkit-touch-callout', 'none');
      canvas.style.userSelect = 'none';
      const mkEl = (css: string) => { const d = document.createElement('div'); d.style.cssText = css; mount.appendChild(d); return d; };
      const R = TOUCH_STICK_R;
      const stickBase = mkEl(`position:absolute;display:none;width:${R * 2}px;height:${R * 2}px;border-radius:50%;` +
        'border:1px solid rgba(150,210,255,0.45);background:rgba(150,210,255,0.08);pointer-events:none;z-index:4;');
      const stickKnob = mkEl('position:absolute;display:none;width:44px;height:44px;border-radius:50%;' +
        'border:1px solid rgba(150,210,255,0.7);background:rgba(150,210,255,0.3);pointer-events:none;z-index:4;');
      const place = (el: HTMLElement, x: number, y: number, half: number) => {
        el.style.left = `${x - half}px`; el.style.top = `${y - half}px`;
      };

      // Three zones, by where a finger lands: left walks, the middle pinches to zoom, the right turns. A
      // finger keeps its job for as long as it stays down, wherever it wanders.
      let moveId = -1, lookId = -1;
      let zoomAId = -1, zoomBId = -1;                  // the two fingers of a pinch, in the middle zone
      let stickX0 = 0, stickY0 = 0, lookX = 0, lookY = 0;
      let zoomAX = 0, zoomAY = 0, zoomBX = 0, zoomBY = 0;
      let zoomD0 = 1, zoomZ0 = 1;                      // finger spacing and zoom when the pinch began
      let midT0 = 0, midTravel = 0;                    // when the first middle finger went down, and how far it has moved (tap versus pinch)
      let midPinched = false, lastTapT = 0;            // that finger took part in a pinch; time of the last tap
      const endStick = () => { moveId = -1; stickVX = 0; stickVY = 0; stickBase.style.display = 'none'; stickKnob.style.display = 'none'; };
      let closeMenu = () => { /* replaced below, once the menu exists */ };

      const onTouchStart = (e: TouchEvent) => {
        e.preventDefault();   // also stops the browser's synthesized mouse events, scrolling and zooming
        if (touchHintRef.current) touchHintRef.current.style.display = 'none';
        closeMenu();   // a touch on the view puts the menu away
        const rect = canvas.getBoundingClientRect();
        const leftEdge = rect.width * TOUCH_ZONE_LEFT, rightEdge = rect.width * TOUCH_ZONE_RIGHT;
        for (const t of Array.from(e.changedTouches)) {
          const lx = t.clientX - rect.left, ly = t.clientY - rect.top;
          if (lx < leftEdge) {
            if (moveId !== -1) continue;
            moveId = t.identifier; stickX0 = lx; stickY0 = ly; stickVX = 0; stickVY = 0;
            place(stickBase, lx, ly, R); place(stickKnob, lx, ly, 22);
            stickBase.style.display = 'block'; stickKnob.style.display = 'block';
          } else if (lx >= rightEdge) {
            if (lookId !== -1) continue;
            lookId = t.identifier; lookX = t.clientX; lookY = t.clientY;
          } else if (zoomAId === -1) {
            zoomAId = t.identifier; zoomAX = t.clientX; zoomAY = t.clientY;
            midT0 = performance.now(); midTravel = 0; midPinched = false;
          } else if (zoomBId === -1) {
            // The second middle finger: a pinch begins.
            zoomBId = t.identifier; zoomBX = t.clientX; zoomBY = t.clientY;
            zoomD0 = Math.max(30, Math.hypot(zoomBX - zoomAX, zoomBY - zoomAY));
            zoomZ0 = zoomT; midPinched = true;
          }
        }
      };
      const onTouchMove = (e: TouchEvent) => {
        e.preventDefault();
        const rect = canvas.getBoundingClientRect();
        for (const t of Array.from(e.changedTouches)) {
          if (t.identifier === moveId) {
            let dx = t.clientX - rect.left - stickX0, dy = t.clientY - rect.top - stickY0;
            const len = Math.hypot(dx, dy);
            if (len > R) { dx *= R / len; dy *= R / len; }
            stickVX = dx / R; stickVY = -dy / R;
            place(stickKnob, stickX0 + dx, stickY0 + dy, 22);
          } else if (t.identifier === lookId) {
            // The turn per pixel shrinks with zoom, so a drag moves the picture the same distance at any zoom.
            yaw   -= (t.clientX - lookX) * TOUCH_LOOK_SENS / zoom;
            pitch  = clampPitch(pitch - (t.clientY - lookY) * TOUCH_LOOK_SENS / zoom);
            lookX = t.clientX; lookY = t.clientY;
          } else if (t.identifier === zoomAId) {
            midTravel += Math.hypot(t.clientX - zoomAX, t.clientY - zoomAY);
            zoomAX = t.clientX; zoomAY = t.clientY;
          } else if (t.identifier === zoomBId) {
            zoomBX = t.clientX; zoomBY = t.clientY;
          }
        }
        if (zoomBId !== -1) {   // fingers apart zooms in, together zooms out
          const d = Math.hypot(zoomBX - zoomAX, zoomBY - zoomAY);
          zoomT = Math.min(TOUCH_ZOOM_MAX, Math.max(1, zoomZ0 * (d / zoomD0)));
        }
      };
      const onTouchEnd = (e: TouchEvent) => {
        e.preventDefault();
        for (const t of Array.from(e.changedTouches)) {
          if (t.identifier === moveId) endStick();
          else if (t.identifier === lookId) lookId = -1;
          else if (t.identifier === zoomAId || t.identifier === zoomBId) {
            const isA = t.identifier === zoomAId;
            const now = performance.now();
            // A short, still single touch in the middle is a tap; two in quick succession put the zoom back to 1.
            if (isA && zoomBId === -1 && !midPinched && now - midT0 < TOUCH_TAP_MS && midTravel < TOUCH_TAP_SLOP) {
              if (now - lastTapT < TOUCH_DOUBLE_TAP_MS) { zoomT = 1; lastTapT = 0; } else lastTapT = now;
            }
            if (!isA) zoomBId = -1;
            else if (zoomBId !== -1) {   // the other finger carries on as the first
              zoomAId = zoomBId; zoomAX = zoomBX; zoomAY = zoomBY; zoomBId = -1;
            } else zoomAId = -1;
          }
        }
      };
      canvas.addEventListener('touchstart',  onTouchStart, { passive: false });
      canvas.addEventListener('touchmove',   onTouchMove,  { passive: false });
      canvas.addEventListener('touchend',    onTouchEnd,   { passive: false });
      canvas.addEventListener('touchcancel', onTouchEnd,   { passive: false });

      // Menu, top right under the tick readout: one hamburger button that opens the rest. The buttons are
      // siblings of the canvas, so touching them never starts the stick or a drag; a touch on the canvas
      // closes the menu (see closeMenu above).
      const bar = mkEl('position:absolute;top:30px;right:10px;display:flex;flex-direction:column;gap:8px;align-items:flex-end;z-index:6;');
      const btnBase = 'padding:6px 10px;background:rgba(0,0,0,0.45);color:rgba(150,210,255,0.85);' +
        'border:1px solid rgba(150,210,255,0.4);border-radius:6px;font:11px "Space Mono",monospace;' +
        'letter-spacing:0.06em;touch-action:manipulation;';
      const menuBtn = document.createElement('button');
      menuBtn.textContent = '☰';
      menuBtn.setAttribute('aria-label', 'Menu');
      menuBtn.style.cssText = btnBase + 'width:40px;height:40px;padding:0;font-size:18px;line-height:1;';
      const panel = document.createElement('div');
      panel.style.cssText = 'display:none;flex-direction:column;gap:8px;align-items:flex-end;';
      bar.appendChild(menuBtn);
      bar.appendChild(panel);
      let menuOpen = false;
      const setMenu = (open: boolean) => {
        menuOpen = open;
        panel.style.display = open ? 'flex' : 'none';
        menuBtn.textContent = open ? '✕' : '☰';
      };
      menuBtn.addEventListener('click', () => setMenu(!menuOpen));
      closeMenu = () => { if (menuOpen) setMenu(false); };
      const mkBtn = (label: string, onTap: () => void) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = btnBase + 'min-width:96px;min-height:36px;';
        b.addEventListener('click', onTap);
        panel.appendChild(b);
        return b;
      };

      // Flashlight: the same toggle as the F key.
      const lightBtn = mkBtn('FLASHLIGHT: OFF', () => {
        flashWanted = !flashWanted;
        lightBtn.textContent = flashWanted ? 'FLASHLIGHT: ON' : 'FLASHLIGHT: OFF';
      });

      // Fullscreen. Where the browser has the Fullscreen API (Android, iPad) the button uses it and then
      // hides the page chrome; iPhone Safari has none for pages, so there the button only hides the chrome
      // (adding the page to the home screen is the way to lose the browser bars on iPhone).
      const root = document.documentElement as any;
      const fsRequest = root.requestFullscreen ?? root.webkitRequestFullscreen;
      const fsElement = (): Element | null => (document as any).fullscreenElement ?? (document as any).webkitFullscreenElement ?? null;
      const fsExit = () => { const d = document as any; return (d.exitFullscreen ?? d.webkitExitFullscreen)?.call(document); };
      const fsBtn = mkBtn('', () => {
        if (!fsRequest) { setUiHidden(!panelHiddenRef.current); syncFsBtn(); return; }
        if (fsElement()) { void fsExit(); return; }
        try {
          Promise.resolve(fsRequest.call(root, { navigationUI: 'hide' }))
            .then(() => (screen.orientation as any)?.lock?.('landscape'))   // Android honours this once fullscreen
            .catch(() => { /* refused: the rotate cover still covers portrait */ });
        } catch { /* ignore */ }
      });
      const syncFsBtn = () => {
        fsBtn.textContent = !fsRequest ? (panelHiddenRef.current ? 'SHOW UI' : 'HIDE UI') : (fsElement() ? 'EXIT FULLSCREEN' : 'FULLSCREEN');
      };
      const onFsChange = () => { setUiHidden(!!fsElement()); syncFsBtn(); };
      document.addEventListener('fullscreenchange', onFsChange);
      document.addEventListener('webkitfullscreenchange', onFsChange);
      syncFsBtn();

      // HI-RES: draw at the screen's native pixel ratio instead of the PIXEL_RATIO_CAP, for sharper grass
      // and foliage at several times the pixel work. Only offered when the screen is denser than the cap.
      // The label shows the resulting render size. Off by default.
      if (nativePixelRatio > PIXEL_RATIO_CAP) {
        let hiRes = false;
        const hiResBtn = mkBtn('HI-RES: OFF', () => {
          hiRes = !hiRes;
          basePixelRatio = hiRes ? nativePixelRatio : Math.min(nativePixelRatio, PIXEL_RATIO_CAP);
          applyQuality();
          const sz = renderer.getDrawingBufferSize(_drawSize);
          hiResBtn.textContent = hiRes ? `HI-RES ${sz.x}x${sz.y}` : 'HI-RES: OFF';
        });
      }

      // Tilt to aim. The offsets are relative: the first sensor reading after switching on (or after a
      // rotation or a LEVEL tap) becomes zero, so there is no compass heading to drift against.
      if ('DeviceOrientationEvent' in window) {
        const _gEuler = new THREE.Euler();
        const _gQ = new THREE.Quaternion();
        const _gQ0 = new THREE.Quaternion();
        const _gQ1 = new THREE.Quaternion(-Math.SQRT1_2, 0, 0, Math.SQRT1_2);   // -90 degrees about x
        const _gZ = new THREE.Vector3(0, 0, 1);
        const _gF = new THREE.Vector3();
        let gyroOn = false, gyroGot = false;
        let gyroBase: { yaw: number; pitch: number } | null = null;

        // Fold the current tilt offsets into the dragged angles and restart from the next reading.
        const bakeGyro = () => {
          yaw += gyroYaw;
          pitch = clampPitch(pitch + gyroPitch);
          gyroYaw = gyroPitch = gyroYawT = gyroPitchT = 0;
          gyroBase = null;
        };
        const onOrient = (e: DeviceOrientationEvent) => {
          if (e.alpha === null || e.beta === null || e.gamma === null) return;
          gyroGot = true;
          const D = Math.PI / 180;
          const screenAngle = (screen.orientation && typeof screen.orientation.angle === 'number')
            ? screen.orientation.angle : ((window as any).orientation ?? 0);
          // Device orientation to a camera quaternion (the same mapping three's old DeviceOrientationControls used),
          // then the direction it looks along, as a yaw and a pitch.
          _gEuler.set(e.beta * D, e.alpha * D, -e.gamma * D, 'YXZ');
          _gQ.setFromEuler(_gEuler).multiply(_gQ1).multiply(_gQ0.setFromAxisAngle(_gZ, -screenAngle * D));
          _gF.set(0, 0, -1).applyQuaternion(_gQ);
          const dYaw = Math.atan2(-_gF.x, -_gF.z);
          const dPitch = Math.asin(Math.max(-1, Math.min(1, _gF.y)));
          // Accumulate the change since the previous reading, shrunk by the zoom: a wrist movement turns the
          // view less when zoomed, so aim stays steady under hand tremor. At zoom 1 the sum equals the pose
          // relative to the one at switch-on.
          if (gyroBase) {
            gyroYawT += wrapPi(dYaw - gyroBase.yaw) / zoom;
            gyroPitchT += (dPitch - gyroBase.pitch) / zoom;
            gyroPitchT = clampPitch(pitch + gyroPitchT) - pitch;   // never push the view past straight up or down
          }
          gyroBase = { yaw: dYaw, pitch: dPitch };
        };
        const tiltBtn = mkBtn('TILT: OFF', async () => {
          if (gyroOn) {
            gyroOn = false;
            window.removeEventListener('deviceorientation', onOrient);
            bakeGyro();
            tiltBtn.textContent = 'TILT: OFF';
            levelBtn.style.display = 'none';
            return;
          }
          try {
            const DOE = (window as any).DeviceOrientationEvent;
            if (typeof DOE.requestPermission === 'function') {   // iOS: needs this tap, and the user's yes
              if ((await DOE.requestPermission()) !== 'granted') { tiltBtn.textContent = 'TILT: DENIED'; return; }
            }
          } catch { tiltBtn.textContent = 'TILT: DENIED'; return; }
          gyroOn = true; gyroGot = false; gyroBase = null;
          window.addEventListener('deviceorientation', onOrient);
          tiltBtn.textContent = 'TILT: ON';
          levelBtn.style.display = 'block';
          setTimeout(() => {
            if (gyroOn && !gyroGot) {   // a device with no motion sensor, or a page that is not on https
              gyroOn = false; window.removeEventListener('deviceorientation', onOrient);
              tiltBtn.textContent = 'TILT: NO SENSOR'; levelBtn.style.display = 'none';
            }
          }, 1500);
        });
        // LEVEL: treat the way the phone is held now as straight ahead, and bring the horizon level.
        const levelBtn = mkBtn('LEVEL', () => { bakeGyro(); pitch = 0; });
        levelBtn.style.display = 'none';

        const rebase = () => { if (gyroOn) bakeGyro(); };   // a rotation of the screen changes the sensor frame
        screen.orientation?.addEventListener?.('change', rebase);
        window.addEventListener('orientationchange', rebase);
        touchUiCleanup.push(() => {
          window.removeEventListener('deviceorientation', onOrient);
          screen.orientation?.removeEventListener?.('change', rebase);
          window.removeEventListener('orientationchange', rebase);
        });
      }

      touchUiCleanup.push(() => {
        canvas.removeEventListener('touchstart',  onTouchStart);
        canvas.removeEventListener('touchmove',   onTouchMove);
        canvas.removeEventListener('touchend',    onTouchEnd);
        canvas.removeEventListener('touchcancel', onTouchEnd);
        document.removeEventListener('fullscreenchange', onFsChange);
        document.removeEventListener('webkitfullscreenchange', onFsChange);
        if (fsElement()) void fsExit();
        stickBase.remove(); stickKnob.remove(); bar.remove();
      });
    }

    // ── Frame decoding ───────────────────────────────────────────────────────
    // Each wire frame is decoded exactly once, in arrival order. The streamed sections (particles,
    // frozen cells) are deltas that must all be applied; fixtures and sites are plain state, so
    // only the newest frame of a batch needs them.
    const seenPositions = new Set<string>();
    const _q = new THREE.Quaternion();
    const _sunDir = new THREE.Vector3(0, 1, 0);
    const lastShadowDir = new THREE.Vector3(0, -1, 0);
    let frozenDirty = false;
    let newFrameApplied = false;
    let decodeMs = 0;

    const applyFixtures = (buf: ArrayBuffer, view: DataView) => {
      const tick = view.getBigUint64(0, true);
      if (tickDisplayRef.current) tickDisplayRef.current.textContent = tick.toString();
      if (clockRef.current) {
        const sp = spinRef.current;
        const txt = sp ? clockText(Number(tick), sp) : '--:--';
        if (clockRef.current.textContent !== txt) clockRef.current.textContent = txt;
      }
      const fixture_count  = view.getUint16(12, true);
      const site_count     = view.getUint16(14, true);
      let off = 20;  // frame header is 20 bytes (particle_upd_count is u32)
      let n = 0;  // sphere fallback instance counter
      instanceIdToOrgIdxRef.current.clear();

      const slotToEntity = slotToEntityRef.current;
      const entityGroups = entityGroupsRef.current;
      const rosterByIdx = rosterByIdxRef.current;
      seenPositions.clear();

      for (let i = 0; i < fixture_count; i++) {
        if (off + RECORD_BYTES > buf.byteLength) break;
        const slotIdx = view.getUint16(off,      true);
        const orgIdx  = view.getUint16(off + 2,  true);
        const x  = view.getFloat32(off + 4,  true);
        const y  = view.getFloat32(off + 8,  true);
        const z  = view.getFloat32(off + 12, true);
        const r  = view.getFloat32(off + 16, true);
        const qw        = view.getFloat32(off + 23, true);
        const qx        = view.getFloat32(off + 27, true);
        const qy        = view.getFloat32(off + 31, true);
        const qz        = view.getFloat32(off + 35, true);
        const geomScale = view.getFloat32(off + 39, true);
        off += RECORD_BYTES;

        // Track world position for each entity (first slot per entity wins)
        const slotInfo = slotToEntity.get(slotIdx);
        const eidForSlot = slotInfo?.entityId ?? rosterByIdx.get(orgIdx)?.entity_id;
        if (eidForSlot && !seenPositions.has(eidForSlot)) {
          seenPositions.add(eidForSlot);
          let ep = entityPositionsRef.current.get(eidForSlot);
          if (!ep) { ep = new THREE.Vector3(); entityPositionsRef.current.set(eidForSlot, ep); }
          ep.set(x, z, -y);
        }

        // If this slot has a schema-rendered group, update its transform.
        if (slotInfo) {
          const groups = entityGroups.get(slotInfo.entityId);
          const group  = groups?.[slotInfo.segIdx];
          if (group) {
            group.position.set(x, z, -y);
            group.quaternion.copy(simQuatToThreeInto(_q, qw, qx, qy, qz));
            group.scale.setScalar(geomScale > 0 ? geomScale : 1);
          }
          continue; // don't also render a fallback sphere
        }

        // Fallback: render as a sphere instance; skip unowned/dead slots
        if (orgIdx === 0xFFFF) continue;
        const orgEntry = rosterByIdx.get(orgIdx);
        if (!orgEntry || orgEntry.state === 'dead') continue;
        if (n >= MAX_FIXTURES) continue;
        _dummy.position.set(x, z, -y);
        _dummy.scale.setScalar(Math.max(0.5, r));
        _dummy.updateMatrix();
        fixtures.setMatrixAt(n, _dummy.matrix);
        fixtures.setColorAt(n, _color.setRGB(0.90, 0.48, 0.15));
        instanceIdToOrgIdxRef.current.set(n, orgIdx);
        n++;
      }

      fixtures.count = n;
      fixtures.instanceMatrix.needsUpdate = true;
      if (fixtures.instanceColor) fixtures.instanceColor.needsUpdate = true;

      // Parse site records from binary (follow all fixture records)
      const registry = siteRegistryRef.current;
      let si = 0;
      for (let i = 0; i < site_count; i++) {
        if (off + 14 > buf.byteLength) break;
        const siteId = view.getUint16(off,      true);
        const sx     = view.getFloat32(off + 2,  true);
        const sy     = view.getFloat32(off + 6,  true);
        const sz     = view.getFloat32(off + 10, true);
        off += 14;
        if (si >= MAX_SITES) continue;
        sitePosArr[si * 3]     = sx;
        sitePosArr[si * 3 + 1] = sz;
        sitePosArr[si * 3 + 2] = -sy;
        const name = registry[siteId] ?? '';
        const isMouth = name.endsWith('_mouth');
        siteColArr[si * 3]     = isMouth ? 0.98 : 0.38;
        siteColArr[si * 3 + 1] = isMouth ? 0.52 : 0.80;
        siteColArr[si * 3 + 2] = isMouth ? 0.18 : 0.98;
        si++;
      }
      siteGeo.setDrawRange(0, si);
      if (si > 0) {
        (siteGeo.attributes.position as THREE.BufferAttribute).needsUpdate = true;
        (siteGeo.attributes.color    as THREE.BufferAttribute).needsUpdate = true;
      }
    };

    // The streamed sections of a frame: particles, then frozen cells. Fixtures and sites come
    // first on the wire at fixed record sizes, so they are skipped by arithmetic.
    const applyStreams = (buf: ArrayBuffer, view: DataView) => {
      const fixture_count    = view.getUint16(12, true);
      const site_count       = view.getUint16(14, true);
      let off = 20 + fixture_count * RECORD_BYTES + site_count * 14;

      // Particle section (managed-field cells, e.g. plants and fruit): 13-byte sub-header
      // [flags u8][a u32][b u32][state_hash u32] then 27-byte records (slot u32, owner u32, xyz,
      // radius, rgb). Two kinds, told apart by flags bit0 (the frozen section below uses the same
      // layout and rules):
      //  - delta (bit0 clear): a = upserted records, b = removed count, state_hash = hash after
      //    applying; then `a` records, then `b` removed slots (u32). Cells not mentioned persist.
      //    Nothing in it can be lost in transit: the server builds each client's delta from its own
      //    position in a change log when the frame is sent.
      //  - keyframe slice (bit0 set): a = records here, b = slice index, state_hash = hash of the whole
      //    set on the last slice. The server cuts a keyframe into slices, one per frame. Bit1 marks the
      //    first slice (start collecting), bit2 the last (swap the collected set in and verify).
      // state_hash is a running XOR of crc32 over each active record. A mismatch, a slice out of order
      // (we dropped a frame) or a delta with no baseline asks the server for a fresh keyframe ("rk"),
      // which restarts only this client's particle stream.
      if (off + PARTICLE_SUBHEADER_BYTES <= buf.byteLength) {
        const pflags = view.getUint8(off);
        const pA = view.getUint32(off + 1, true);
        const pB = view.getUint32(off + 5, true);
        const frameHash = view.getUint32(off + 9, true);
        off += PARTICLE_SUBHEADER_BYTES;
        const recBase = off;
        const isKeyframe = (pflags & 0x01) !== 0;
        // Always step past the whole section, applied or not, so the frozen section after it parses.
        off += pA * PARTICLE_RECORD_BYTES + (isKeyframe ? 0 : pB * 4);
        const requestParticleKeyframe = () => {
          particleCrcFrameRef.current++;
          if (particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
            try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
            lastRkFrameRef.current = particleCrcFrameRef.current;
          }
        };
        const fruitAt = (o: number): FruitRec => [
          view.getFloat32(o + 8, true), view.getFloat32(o + 12, true), view.getFloat32(o + 16, true),
          view.getFloat32(o + 20, true),
          view.getUint8(o + 24), view.getUint8(o + 25), view.getUint8(o + 26),
          view.getUint32(o + 4, true),
        ];
        if (isKeyframe) {
          if ((pflags & 0x02) !== 0) {          // first slice: start collecting
            particleKfRef.current = { shadow: new Map(), fruit: new Map(), hash: 0, next: 0 };
            particleHasKeyframeRef.current = false;
          }
          const kf = particleKfRef.current;
          if (kf && pB === kf.next) {
            let o = recBase;
            for (let i = 0; i < pA; i++) {
              if (o + PARTICLE_RECORD_BYTES > buf.byteLength) break;
              const slot = view.getUint32(o, true);
              if (view.getUint32(o + 4, true) >= PROP_OWNER_MIN) kf.fruit.set(slot, fruitAt(o));
              // Hash over a VIEW into the frame buffer (no copy) and keep only the number.
              const crc = crc32(new Uint8Array(buf, o, PARTICLE_RECORD_BYTES));
              kf.shadow.set(slot, crc);
              kf.hash = (kf.hash ^ crc) >>> 0;
              o += PARTICLE_RECORD_BYTES;
            }
            kf.next++;
            particleCrcFrameRef.current++;      // slices count as frames for the request cooldown
            if ((pflags & 0x04) !== 0) {        // last slice: the keyframe is complete, swap it in
              particleRawRef.current = kf.shadow;
              particleHashRef.current = kf.hash;
              const fruitLive = fruitLiveRef.current;
              fruitLive.clear();
              for (const [k, v] of kf.fruit) fruitLive.set(k, v);
              if (fruitRef.current && frondRef.current) flushProps(fruitRef.current, frondRef.current, fruitLive);
              particleKfRef.current = null;
              particleHasKeyframeRef.current = true;
              if (kf.hash !== frameHash) requestParticleKeyframe();
            }
          } else {
            // A slice we cannot place (we dropped the ones before it): abandon this keyframe and ask
            // for a new one. Slices still in flight from the old one are ignored until its first
            // slice arrives.
            particleKfRef.current = null;
            particleHasKeyframeRef.current = false;
            requestParticleKeyframe();
          }
        } else if (particleHasKeyframeRef.current) {
          const rawShadow = particleRawRef.current;
          const fruitLive = fruitLiveRef.current;
          let fruitChanged = false;
          let o = recBase;
          // Plant cells are not drawn, so a record only feeds the running hash; fruit and frond debris are also kept for drawing.
          for (let i = 0; i < pA; i++) {
            if (o + PARTICLE_RECORD_BYTES > buf.byteLength) break;
            const slot = view.getUint32(o, true);
            if (view.getUint32(o + 4, true) >= PROP_OWNER_MIN) {
              fruitLive.set(slot, fruitAt(o));
              fruitChanged = true;
            } else if (fruitLive.delete(slot)) {
              fruitChanged = true;               // the slot was a fruit and is now something else
            }
            const newCrc = crc32(new Uint8Array(buf, o, PARTICLE_RECORD_BYTES));
            const prev = rawShadow.get(slot);
            if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ prev) >>> 0;
            particleHashRef.current = (particleHashRef.current ^ newCrc) >>> 0;
            rawShadow.set(slot, newCrc);
            o += PARTICLE_RECORD_BYTES;
          }
          for (let i = 0; i < pB; i++) {
            if (o + 4 > buf.byteLength) break;
            const slot = view.getUint32(o, true);
            o += 4;
            if (fruitLive.delete(slot)) fruitChanged = true;
            const prev = rawShadow.get(slot);
            if (prev !== undefined) particleHashRef.current = (particleHashRef.current ^ prev) >>> 0;
            rawShadow.delete(slot);
          }
          if (fruitChanged && fruitRef.current && frondRef.current) flushProps(fruitRef.current, frondRef.current, fruitLive);
          // O(1) audit: our hash vs the server's. A mismatch means we are not in step (a frame was
          // dropped on our side); the cooldown avoids a storm.
          particleCrcFrameRef.current++;
          if (particleHashRef.current !== frameHash &&
              particleCrcFrameRef.current - lastRkFrameRef.current >= PARTICLE_RK_COOLDOWN) {
            try { wsRef.current?.send("rk"); } catch { /* socket not ready */ }
            lastRkFrameRef.current = particleCrcFrameRef.current;
          }
        } else if (particleKfRef.current === null) {
          // A delta with no baseline and no keyframe in progress: unusable, so ask for a keyframe (throttled).
          requestParticleKeyframe();
        }
      }

      // Frozen section (LOD unit cells): 13-byte sub-header [flags u8][a u32][b u32][hash u32], then
      // 36-byte records (layout in frozenTrees.ts: cid, owner, xyz, parent delta, radii, cell type,
      // progress, rgb, flags). Two kinds, told apart by flags bit0:
      //  - delta (bit0 clear): a = added count, b = removed count, hash = cid-set hash after applying;
      //    then `a` records, then `b` removed cids. Nothing in it can be lost in transit: the server
      //    builds each client's delta from its own position in a change log when the frame is sent.
      //  - keyframe slice (bit0 set): a = records here, b = slice index, hash = whole-set hash on the
      //    last slice. The server cuts a keyframe into slices, one per frame, so a big forest arrives
      //    over many frames instead of as one multi-megabyte message. Bit1 marks the first slice
      //    (start collecting cids), bit2 the last (drop held cells the keyframe lacks, then verify the
      //    hash). Frozen cells are immutable, so a slice only builds cells we do not already hold.
      // The hash is a running XOR of crc32(cid) over the active set; a mismatch, a slice out of
      // order (a dropped frame on our side) or a delta with no baseline asks the server for a fresh
      // keyframe ("rfk"), which restarts only this client's stream.
      const fmesh = frozenRef.current;
      if (fmesh && off + FROZEN_SUBHEADER_BYTES <= buf.byteLength) {
        const fflags = view.getUint8(off);
        const addedCount = view.getUint32(off + 1, true);
        const fB = view.getUint32(off + 5, true);
        const fFrameHash = view.getUint32(off + 9, true);
        off += FROZEN_SUBHEADER_BYTES;
        const isFKeyframe = (fflags & 0x01) !== 0;
        const requestFrozenKeyframe = () => {
          frozenCrcFrameRef.current++;
          if (frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
            try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
            lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
          }
        };
        if (isFKeyframe) {
          if ((fflags & 0x02) !== 0) {          // first slice: start collecting
            frozenKfKeepRef.current = new Set<number>();
            frozenKfNextRef.current = 0;
            frozenHashRef.current = 0;          // records below re-accumulate it
            frozenHasKeyframeRef.current = false;
          }
          const keep = frozenKfKeepRef.current;
          if (keep && fB === frozenKfNextRef.current) {
            for (let i = 0; i < addedCount; i++) {
              if (off + FROZEN_RECORD_BYTES > buf.byteLength) break;
              const cid = view.getUint32(off, true);
              keep.add(cid);
              if (fmesh.has(cid)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              else if (fmesh.add(view, off)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
              off += FROZEN_RECORD_BYTES;
            }
            frozenKfNextRef.current++;
            frozenCrcFrameRef.current++;   // slices count as frames for the request cooldown
            frozenDirty = true;     // the store is flushed to the GPU once per batch, after the loop
            if ((fflags & 0x04) !== 0) {        // last slice: the keyframe is complete
              fmesh.retainOnly(keep);
              frozenKfKeepRef.current = null;
              frozenHasKeyframeRef.current = true;
              if (frozenHashRef.current !== fFrameHash) requestFrozenKeyframe();
            }
          } else {
            // A slice we cannot place (we dropped the ones before it): abandon this keyframe and ask
            // for a new one. Slices still in flight from the old one are ignored until its first
            // slice arrives.
            frozenKfKeepRef.current = null;
            frozenHasKeyframeRef.current = false;
            requestFrozenKeyframe();
          }
        } else if (frozenHasKeyframeRef.current) {
          for (let i = 0; i < addedCount; i++) {
            if (off + FROZEN_RECORD_BYTES > buf.byteLength) break;
            const cid = view.getUint32(off, true);
            // xor in only on genuine insert, so a re-sent cid does not double-count.
            if (fmesh.add(view, off)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
            off += FROZEN_RECORD_BYTES;
          }
          for (let i = 0; i < fB; i++) {
            if (off + 4 > buf.byteLength) break;
            const cid = view.getUint32(off, true);
            off += 4;
            // xor out only when we actually held it, keeping the hash in step.
            if (fmesh.remove(cid)) frozenHashRef.current = (frozenHashRef.current ^ crc32Cid(cid)) >>> 0;
          }
          frozenDirty = true;
          // O(1) audit: our cid-set hash vs the server's. A mismatch means we are not in step
          // (the frame was dropped on our side); the cooldown avoids a storm.
          frozenCrcFrameRef.current++;
          if (frozenHashRef.current !== fFrameHash &&
              frozenCrcFrameRef.current - lastFrozenRkFrameRef.current >= FROZEN_RK_COOLDOWN) {
            try { wsRef.current?.send("rfk"); } catch { /* socket not ready */ }
            lastFrozenRkFrameRef.current = frozenCrcFrameRef.current;
          }
        } else if (frozenKfKeepRef.current === null) {
          // A delta with no baseline and no keyframe in progress (we dropped frames, or missed the
          // start): deltas are unusable, so ask for a keyframe (throttled). Frozen is the last
          // section, so leaving `off` mid-section is fine.
          requestFrozenKeyframe();
        }
      }
    };

    // ── Sun motion: dead reckoning ───────────────────────────────────────────
    // Sun messages arrive at the sim's cadence, a few per second to dozens, and the frame loop runs at
    // its own rate, so drawing "the latest message" or easing between messages moves the sky in fits:
    // some frames carry two steps of motion and some none. Instead the sky moves continuously. Its
    // velocity per sim tick comes from the messages themselves (the newest against one about a second
    // older, so rounding in the message does not show), the sim tick rate from the stats message turns
    // that into velocity per second, and each frame draws the newest message plus the time since it
    // arrived times that velocity. When the next message says the prediction was a little off, the
    // difference is worked off over a fraction of a second instead of jumping, so the motion stays
    // smooth whatever the message cadence. A gap longer than SUN_STALL_S (a hidden tab, a paused or
    // stalled sim, a restart) is not worked off: whatever happened in it is dropped and the sky
    // goes straight to the latest message.
    type SunState = NonNullable<typeof sunRef.current>;
    const SUN_STALL_S = 1.5, SUN_BLEND_S = 0.3, SUN_HIST_S = 1.0, SUN_LEAD_MAX_S = 0.5;
    const ID9 = [1, 0, 0, 0, 1, 0, 0, 0, 1];
    const SUN_N = 31;
    // Fixed layout: 0-2 direction, 3 elevation, 4 angular diameter, 5-7 direct rgb, 8-10 sky rgb,
    // 11-13 world position, 14-22 frame, 23-25 moon direction, 26 moon size, 27 moon phase, 28-30 moon rgb.
    const packSun = (s: SunState): number[] => [
      ...s.direction, s.elevation, s.angularDiameter, ...s.directRgb, ...s.skyRgb,
      ...(s.worldPos ?? [0, 0, 0]), ...(s.frame && s.frame.length === 9 ? s.frame : ID9),
      ...(s.moon ? [...s.moon.direction, s.moon.angularDiameter, s.moon.phase, ...s.moon.rgb] : [0, 0, 0, 0, 0, 0, 0, 0]),
    ];
    const sunHas = (s: SunState) => (s.worldPos ? 1 : 0) | (s.frame && s.frame.length === 9 ? 2 : 0) | (s.moon ? 4 : 0);
    const sunShown: SunState = { direction: [0, 0, 1], elevation: 0, angularDiameter: 0, directRgb: [0, 0, 0], skyRgb: [0, 0, 0], isDay: true, moon: null };
    interface SunSample { tick: number; wall: number; v: number[]; has: number; isDay: boolean }
    let sunSeen: SunState | null = null;           // the last message taken in
    let sunHist: SunSample[] = [];
    let sunVel: number[] = new Array(SUN_N).fill(0);   // change per sim tick
    let sunErr: number[] = new Array(SUN_N).fill(0);   // correction still to work off
    let sunErrT0 = 0;
    const sunEval = (nowMs: number): number[] | null => {
      const n = sunHist[sunHist.length - 1];
      if (!n) return null;
      const tps = simTpsRef.current;
      const lead = tps > 0 ? Math.min(Math.max(0, (nowMs - n.wall) / 1000), SUN_LEAD_MAX_S) * tps : 0;
      const k = Math.exp(-Math.max(0, nowMs - sunErrT0) / 1000 / SUN_BLEND_S);
      const out = new Array<number>(SUN_N);
      for (let i = 0; i < SUN_N; i++) out[i] = n.v[i] + sunVel[i] * lead + sunErr[i] * k;
      return out;
    };
    const smoothSun = (nowMs: number): SunState | null => {
      const target = sunRef.current;
      if (!target) return null;
      if (target !== sunSeen) {
        sunSeen = target;
        const was = sunEval(nowMs);                          // where the old model has the sky right now
        const s: SunSample = { tick: target.tick ?? NaN, wall: target.receivedAt ?? nowMs, v: packSun(target), has: sunHas(target), isDay: target.isDay };
        const prev = sunHist[sunHist.length - 1];
        if (!prev || !Number.isFinite(s.tick) || s.tick < prev.tick || (s.wall - prev.wall) / 1000 > SUN_STALL_S || s.has !== prev.has) {
          sunHist = [s]; sunVel.fill(0); sunErr.fill(0);     // first message, a stall or a restart: just take it
        } else {
          sunHist.push(s);
          while (sunHist.length > 2 && s.wall - sunHist[1].wall >= SUN_HIST_S * 1000) sunHist.shift();
          const o = sunHist[0], dtk = s.tick - o.tick;
          if (dtk > 0) for (let i = 0; i < SUN_N; i++) sunVel[i] = (s.v[i] - o.v[i]) / dtk;
          const pred = sunEval(nowMs);
          if (was && pred) { for (let i = 0; i < SUN_N; i++) sunErr[i] = was[i] - (pred[i] - sunErr[i] * Math.exp(-Math.max(0, nowMs - sunErrT0) / 1000 / SUN_BLEND_S)); sunErrT0 = nowMs; }
        }
      }
      const out = sunEval(nowMs);
      const n = sunHist[sunHist.length - 1];
      if (!out || !n) return null;
      const unit = (o: number): [number, number, number] => {
        const l = Math.hypot(out[o], out[o + 1], out[o + 2]);
        return l > 1e-3 ? [out[o] / l, out[o + 1] / l, out[o + 2] / l] : [out[o], out[o + 1], out[o + 2]];
      };
      const [dx, dy, dz] = unit(0);
      sunShown.direction[0] = dx; sunShown.direction[1] = dy; sunShown.direction[2] = dz;
      sunShown.elevation = out[3];
      sunShown.angularDiameter = Math.max(0, out[4]);
      for (let i = 0; i < 3; i++) { sunShown.directRgb[i] = Math.max(0, out[5 + i]); sunShown.skyRgb[i] = Math.max(0, out[8 + i]); }
      sunShown.isDay = n.isDay;
      sunShown.worldPos = n.has & 1 ? [out[11], out[12], out[13]] : undefined;
      sunShown.frame = n.has & 2 ? out.slice(14, 23) : undefined;
      sunShown.moon = n.has & 4 ? {
        direction: unit(23), angularDiameter: Math.max(0, out[26]), phase: out[27],
        rgb: [Math.max(0, out[28]), Math.max(0, out[29]), Math.max(0, out[30])],
      } : null;
      return sunShown;
    };

    // ── rAF loop ─────────────────────────────────────────────────────────────
    let animId: number;
    let lastPickMs = 0;
    let lastShadowMs = 0;
    let shadowUpdatesWin = 0;
    let statsAt = 0;
    let frameEma = 16;            // smoothed frame time, ms (each sample capped so one hitch does not count as slow)
    let winFrames = 0, winMs = 0; // true frame count and time since the overlay last refreshed
    let fpsClock = 0;             // seconds since the quality level last changed
    let calmFor = 0;              // seconds frames have been fast
    let upWait = 6;               // seconds of calm before stepping quality back up (doubles if that step bounces back)
    let lastRaiseAt = -1e9;
    let tSec = 0;
    // Lowering the resolution only helps when the GPU is fill-bound. When the frame is slow for some
    // other reason (draw calls, JS, stream decoding) it just makes the picture blurry for nothing. So
    // each step down is checked a few seconds later: if frame time did not drop by at least 7 percent
    // the step is undone and further steps down are held off for two minutes.
    let probing = false, probeFromMs = 0, holdDownUntil = 0;

    const updateOverlay = (nowMs: number) => {
      const secs = (nowMs - statsAt) / 1000;
      statsAt = nowMs;
      const info = renderer.info.render;
      const fm = frozenRef.current, gr = grassRef.current;
      const sz = renderer.getDrawingBufferSize(new THREE.Vector2());
      const lv = QUALITY_LEVELS[qLevel];
      const f = (n: number) => n >= 1e6 ? (n / 1e6).toFixed(2) + 'M' : n >= 1e3 ? (n / 1e3).toFixed(1) + 'k' : String(n);
      const on = (b: boolean) => (b ? 'on ' : 'OFF');
      dbg.textContent =
        `FPS ${(winFrames * 1000 / Math.max(winMs, 1)).toFixed(1)}   frame ${(winMs / Math.max(winFrames, 1)).toFixed(1)} ms   decode ${decodeMs.toFixed(1)} ms   queued ${frameQueueRef.current.length}\n` +
        `render ${sz.x}x${sz.y}   quality L${qLevel} (res x${lv.res.toFixed(2)}, grass ${(lv.grass * 100).toFixed(0)}%)   auto ${autoQuality ? 'on' : 'off'}\n` +
        `draw calls ${info.calls}   triangles ${f(info.triangles)}   geometries ${renderer.info.memory.geometries}   textures ${renderer.info.memory.textures}\n` +
        `trees: cells ${f(fm?.count ?? 0)}  wood ${f(fm?.woodCount ?? 0)}  sprigs ${f(fm?.sprigCount ?? 0)} (submitted ${f(fm?.sprigsDrawn ?? 0)})  bins ${fm?.binCount ?? 0}  chunks ${fm?.chunkCount ?? 0}\n` +
        `exposure gain ${exposure.toExponential(1)}   stars ${starFieldRef.current?.count ?? 0}   sun msgs ${(sunMsgCountRef.current / Math.max(secs, 1e-3)).toFixed(1)}/s   sim ${simTpsRef.current.toFixed(1)} tps\n` +
        `grass: ${f(gr?.tufts ?? 0)} tufts   canopy easing ${canopyShadeRef.current?.easing ? 'yes' : 'no '}   shadow refresh ${(shadowUpdatesWin / Math.max(secs, 1e-3)).toFixed(0)}/s\n` +
        `[1] grass ${on(show.grass)}  [2] trees ${on(show.trees)}  [3] shadows ${on(show.shadows)}  [4] rocks ${on(show.rocks)}  [5] auto quality  [ ] quality  [F] flashlight`;
      shadowUpdatesWin = 0; winFrames = 0; winMs = 0; sunMsgCountRef.current = 0;
    };

    function animate() {
      animId = requestAnimationFrame(animate);
      const dt = clock.getDelta();
      const nowMs = performance.now();
      tSec += dt;
      windRef.current?.update(dt, simTpsRef.current);
      frozenRef.current?.update(dt);

      // Quality controller: step down when smoothed frame time is slow, back up after a stretch of
      // fast frames (vsync caps a healthy frame at about 16.7 ms, so "fast" means "holding 60").
      // A step back up that bounces straight down again doubles the calm period required next time.
      frameEma += (Math.min(dt * 1000, 60) - frameEma) * 0.08;
      winFrames++; winMs += dt * 1000;
      fpsClock += dt;
      if (autoQuality) {
        if (probing && fpsClock > 3) {
          probing = false;
          if (frameEma > 0.93 * probeFromMs && qLevel > 0) {
            qLevel--; applyQuality(); fpsClock = 0; calmFor = 0; holdDownUntil = tSec + 120;
          }
        } else if (frameEma > SLOW_FRAME_MS && qLevel < QUALITY_LEVELS.length - 1 && fpsClock > 1.5 && tSec >= holdDownUntil && !probing) {
          if (tSec - lastRaiseAt < 15) upWait = Math.min(60, upWait * 2);
          probing = true; probeFromMs = frameEma;
          qLevel++; applyQuality(); fpsClock = 0; calmFor = 0;
        } else if (frameEma < FAST_FRAME_MS && qLevel > 0) {
          calmFor += dt;
          if (calmFor > upWait && fpsClock > upWait) { qLevel--; applyQuality(); fpsClock = 0; calmFor = 0; lastRaiseAt = tSec; }
        } else {
          calmFor = 0;
        }
      }

      // Decode queued frames in order, within a time budget (a long backlog carries over to the
      // next animation frame rather than freezing this one). Only the newest frame decoded in this
      // batch updates the fixtures and sites; every frame's particle and frozen sections are applied.
      const queue = frameQueueRef.current;
      if (queue.length > 0) {
        const t0 = performance.now();
        let last: { buf: ArrayBuffer; view: DataView } | null = null;
        while (queue.length > 0) {
          const f = queue.shift()!;
          applyStreams(f.buf, f.view);
          last = f;
          if (performance.now() - t0 > FRAME_BUDGET_MS) break;
        }
        if (frozenDirty) { frozenRef.current?.flush(); frozenDirty = false; }
        if (last) { applyFixtures(last.buf, last.view); newFrameApplied = true; }
        decodeMs = performance.now() - t0;
      }

      // Hover highlight: swap material color (MeshBasicMaterial has no emissive)
      const NEON_BASE    = new THREE.Color(0.90, 0.48, 0.15);
      const NEON_HOVER   = new THREE.Color(1.0, 0.82, 0.40);
      const currentHighlight = hoveredEntityIdRef.current;
      if (currentHighlight !== prevHighlightedId) {
        if (prevHighlightedId !== undefined) {
          const prevGroups = entityGroupsRef.current.get(prevHighlightedId);
          if (prevGroups) {
            for (const g of prevGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_BASE);
              });
            }
          }
        }
        if (currentHighlight !== undefined) {
          const newGroups = entityGroupsRef.current.get(currentHighlight);
          if (newGroups) {
            for (const g of newGroups) {
              g.traverse(obj => {
                if (obj instanceof THREE.Mesh) (obj.material as THREE.MeshStandardMaterial).color.copy(NEON_HOVER);
              });
            }
          }
        }
        prevHighlightedId = currentHighlight;
      }

      // Hover from the crosshair while locked: report whatever the center ray hits.
      if (locked && nowMs - lastPickMs >= PICK_INTERVAL_MS) {
        lastPickMs = nowMs;
        const hid = pickFromCenter();
        if (hid !== prevHoveredFromCanvas) {
          prevHoveredFromCanvas = hid;
          onOrganismHoverRef.current?.(hid);
        }
      }

      // Zoom: ease toward the pinch target and set the field of view to magnify by that factor (a longer lens
      // scales the tangent of the half angle).
      if (zoom !== zoomT) {
        zoom += (zoomT - zoom) * (1 - Math.exp(-dt / ZOOM_EASE_S));
        if (Math.abs(zoomT - zoom) < 1e-3) zoom = zoomT;
        camera.fov = 2 * Math.atan(Math.tan(baseFov * Math.PI / 360) / zoom) * 180 / Math.PI;
        camera.updateProjectionMatrix();
      }

      // View direction: the dragged or mouse-turned angles plus the eased tilt offsets (zero unless tilt
      // is on, so the mouse path is unchanged).
      {
        const gk = 1 - Math.exp(-dt / GYRO_EASE_S);
        gyroYaw += wrapPi(gyroYawT - gyroYaw) * gk;
        gyroPitch += (gyroPitchT - gyroPitch) * gk;
        camera.rotation.set(clampPitch(pitch + gyroPitch), yaw + gyroYaw, 0, 'YXZ');
      }

      // ── Camera: follow-cam when snapped, else FPS walk at fixed eye height ────
      const snapId = snapToEntityIdRef.current;
      if (snapId) {
        // Follow-cam: keep camera behind and above the snapped entity.
        const targetPos = entityPositionsRef.current.get(snapId);
        if (targetPos) {
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 0.001) _fwd.normalize(); else _fwd.set(0, 0, -1);
          camera.position.set(
            targetPos.x - _fwd.x * 12,
            targetPos.y + 6,
            targetPos.z - _fwd.z * 12,
          );
        }
      } else {
        // FPS walk: translate along the flattened look direction, pinned to eye height.
        if (locked) {
          const sprint = keys.has('ShiftLeft') || keys.has('ShiftRight') ? SPRINT_MULT : 1;
          const step = MOVE_SPEED * sprint * dt;
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 1e-6) _fwd.normalize();
          _right.crossVectors(_fwd, camera.up).normalize();  // right of the look direction
          _move.set(0, 0, 0);
          if (keys.has('KeyW')) _move.add(_fwd);
          if (keys.has('KeyS')) _move.sub(_fwd);
          if (keys.has('KeyD')) _move.add(_right);
          if (keys.has('KeyA')) _move.sub(_right);
          if (_move.lengthSq() > 1e-6) {
            _move.normalize().multiplyScalar(step);
            camera.position.x += _move.x;
            camera.position.z += _move.z;
          }
        }
        // Touch walk: the stick sets a target velocity (analog, no sprint) that the walking speed eases
        // toward: a gentle ramp up, a quick stop. The speed is divided by the zoom, because a magnified view
        // makes the same walk look that much faster, and that mismatch is a motion-sickness trigger.
        // Direction follows where the view points, tilt included.
        if (isTouch) {
          camera.getWorldDirection(_fwd);
          _fwd.y = 0;
          if (_fwd.lengthSq() > 1e-6) _fwd.normalize();
          _right.crossVectors(_fwd, camera.up).normalize();
          const mag = Math.hypot(stickVX, stickVY);
          let tR = 0, tF = 0;
          if (mag > TOUCH_DEADZONE) {
            const s = Math.min(1, (mag - TOUCH_DEADZONE) / (1 - TOUCH_DEADZONE));
            tR = (stickVX / mag) * s * TOUCH_WALK_SPEED / zoom;
            tF = (stickVY / mag) * s * TOUCH_WALK_SPEED / zoom;
          }
          // The easing is short on any axis whose speed is falling.
          const ekR = 1 - Math.exp(-dt / (Math.abs(tR) < Math.abs(walkR) ? TOUCH_STOP_TAU : TOUCH_ACCEL_TAU));
          const ekF = 1 - Math.exp(-dt / (Math.abs(tF) < Math.abs(walkF) ? TOUCH_STOP_TAU : TOUCH_ACCEL_TAU));
          walkR += (tR - walkR) * ekR;
          walkF += (tF - walkF) * ekF;
          if (Math.abs(walkR) + Math.abs(walkF) > 1e-3) {
            camera.position.x += (_fwd.x * walkF + _right.x * walkR) * dt;
            camera.position.z += (_fwd.z * walkF + _right.z * walkR) * dt;
          }
        }
        // pin eye height above the ground every frame (the ground is the terrain surface)
        camera.position.y = EYE_HEIGHT + (terrainRef.current?.heightAtThree(camera.position.x, camera.position.z) ?? 0);
      }
      grassRef.current?.update(camera.position);
      frozenRef.current?.setView(camera.position);

      // ── Sun: drive light, sky, exposure and the sun disc from sun state ──────
      const sun = smoothSun(nowMs);
      if (sun) {
        // The server publishes the sun's LOCAL direction (this world's frame). Sim
        // direction (dx, dy, dz), dz = up, maps to three (dx, dz, -dy).
        const [dx, dy, dz] = sun.direction;
        const tdx = dx, tdy = dz, tdz = -dy;
        _sunDir.set(tdx, tdy, tdz);
        const dRgb = sun.directRgb;
        const sRgb = sun.skyRgb;

        // Exposure: luminance of the open-ground illumination (sky plus the sun's beam on a
        // horizontal surface) sets the gain.
        const sinEl = Math.max(0, Math.sin(sun.elevation));
        const lum = 0.2126 * (sRgb[0] + dRgb[0] * sinEl)
                  + 0.7152 * (sRgb[1] + dRgb[1] * sinEl)
                  + 0.0722 * (sRgb[2] + dRgb[2] * sinEl);
        const adapt = exposureGain(lum);
        exposure = adapt;
        renderer.toneMappingExposure = BASE_EXPOSURE * adapt;

        // Light for shading: the beam is a directional light just outside the patch
        // (shadow-map placement only; rays are parallel), skylight is the ambient.
        // Three applies the cosine term itself, so the colour is the beam-normal value.
        const LIGHT_DIST = worldMax * 2;
        sunLight.position.set(cx + tdx * LIGHT_DIST, tdy * LIGHT_DIST, cz + tdz * LIGHT_DIST);
        sunLight.target.position.set(cx, 0, cz);
        sunLight.color.setRGB(dRgb[0], dRgb[1], dRgb[2]);
        sunLight.intensity = sun.elevation > 0 ? LIGHT_GAIN : 0;
        ambient.color.setRGB(sRgb[0], sRgb[1], sRgb[2]);
        ambient.intensity = AMBIENT_GAIN;

        // Sky and fog from skylight, compressed to display range. At night the colour first goes
        // through the same scotopic shift as the lit surfaces, so sky and ground agree.
        const skyView = scotopicShift(sRgb, BASE_EXPOSURE * adapt);
        _skyColor.setRGB(
          1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[0]),
          1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[1]),
          1 - Math.exp(-SKY_EXPOSURE * adapt * skyView[2]),
        );
        (scene.background as THREE.Color).copy(_skyColor);
        scene.fog!.color.copy(_skyColor);

        // Sun disc at infinity: camera-locked, angular radius from the server. Colour is
        // the beam's hue (normalised); held while the beam is 0 so the last sliver of
        // limb at the horizon keeps its colour. Visible while the upper limb is up.
        const dMax = Math.max(dRgb[0], dRgb[1], dRgb[2]);
        if (dMax > 1e-12) lastDisc.setRGB(dRgb[0] / dMax, dRgb[1] / dMax, dRgb[2] / dMax);
        const angR = sun.angularDiameter * 0.5;
        const orbR = SKY_R * Math.tan(angR) * SUN_SIZE_SCALE;
        const ox = camera.position.x + tdx * SKY_R;
        const oy = camera.position.y + tdy * SKY_R;
        const oz = camera.position.z + tdz * SKY_R;
        sunOrb.position.set(ox, oy, oz);
        sunOrb.scale.setScalar(orbR);
        // The disc is nearly white even in a warm beam (it is far brighter than the screen can show);
        // the glow keeps more of the hue.
        sunOrbMat.color.copy(lastDisc).lerp(_white, 0.5);
        glareColor.copy(lastDisc).lerp(_white, 0.25);
        // Glare grows with how bright the beam is (a reddened, dim low sun glows less).
        const dLum = 0.2126 * dRgb[0] + 0.7152 * dRgb[1] + 0.0722 * dRgb[2];
        sunBeamLum = dLum * sinEl;
        const vis = sun.elevation > -angR;
        sunOrb.visible = vis;
        sunGlare.update(camera.position, ox, oy, oz, orbR, glareColor, 0.8 * (dLum / (dLum + 0.02)), vis, srgbOut);
        horizonMesh.position.set(camera.position.x, HORIZON_Y, camera.position.z);

        // The moon, shaded from the sun's direction, and the catalog stars placed from the world's
        // own position and frame.
        _sunDirThree.set(tdx, tdy, tdz);
        moonView.update(camera.position, sun.moon ?? null, _sunDirThree, adapt, srgbOut);
        const drawH = renderer.getDrawingBufferSize(_drawSize).y;
        starField.update(camera,
          sun.worldPos && sun.frame ? { worldPos: sun.worldPos, frame: sun.frame } : null,
          adapt, tSec, drawH, srgbOut, renderer.getPixelRatio());
      }

      // ── Sea: tide, wet sand, and the underwater look ──────────────────────────
      // Everything here is cosmetic and client-side. The tide comes from the sun and moon directions in
      // the sun message (tide.ts); the wet band on the sand follows the tide up at once and down slowly;
      // with the eye under the surface the fog and sky become the colour of the water.
      {
        const tv = terrainRef.current;
        if (tv && tv.hasSea) {
          if (!seaPlaced) {
            // Start on the beach, a few metres behind the first dry sand along the island's south side,
            // facing the sea (sim -y is three +z, so yaw pi).
            seaPlaced = true;
            const dry = tv.seaLevel + tv.tideAmplitude + SEA_DRY_MARGIN;
            let sy = 0;
            for (let y = 0; y < worldH; y += 0.5) { if (tv.heightAtThree(cx, -y) >= dry) { sy = y; break; } }
            camera.position.set(cx, EYE_HEIGHT, -(sy + 6));
            yaw = Math.PI; pitch = -0.08;
            camera.rotation.set(pitch, yaw, 0, 'YXZ');
          }
          const tide = sun ? tideOffset(sun.direction, sun.moon ? sun.moon.direction : null, tv.tideAmplitude) : 0;
          const seaY = tv.seaLevel + tide;
          wetY = Math.max(seaY, wetY + (seaY - wetY) * (1 - Math.exp(-dt / WET_DRY_TAU_S)));
          tv.setWetLevel(wetY);
          const eyeDepth = seaY - camera.position.y;
          const uw = THREE.MathUtils.clamp(eyeDepth / UNDER_FADE_M, 0, 1);
          _underCol.copy(_skyColor).multiply(_underTint);
          water.setEnabled(true);
          // Glitter: each light's strength in the sky curve's units, 1 - exp(-K * ...) in the shader.
          if (sun) {
            const kg = SKY_EXPOSURE * exposure;
            _glitSunK[0] = kg * sun.directRgb[0]; _glitSunK[1] = kg * sun.directRgb[1]; _glitSunK[2] = kg * sun.directRgb[2];
            const m = sun.moon;
            if (m) {
              _glitMoonDir.set(m.direction[0], m.direction[2], -m.direction[1]);     // sim (x, y, z) -> three (x, z, -y)
              _glitMoonK[0] = kg * m.rgb[0]; _glitMoonK[1] = kg * m.rgb[1]; _glitMoonK[2] = kg * m.rgb[2];
            }
            water.update(camera.position, seaY, tSec, _skyColor, eyeDepth > 0, _underCol,
              { sunDir: _sunDir, sunK: _glitSunK, moonDir: m ? _glitMoonDir : null, moonK: _glitMoonK });
          } else {
            water.update(camera.position, seaY, tSec, _skyColor, eyeDepth > 0, _underCol);
          }
          const fog = scene.fog as THREE.FogExp2;
          fog.density = THREE.MathUtils.lerp(FOG_AIR_DENSITY, FOG_UNDER_DENSITY, uw);
          if (uw > 0) {
            _fogMix.copy(_skyColor).lerp(_underCol, uw);
            fog.color.copy(_fogMix);
            (scene.background as THREE.Color).copy(_fogMix);
          }
        } else {
          water.setEnabled(false);
        }
      }

      // Flashlight: fade, follow the camera, and lift the canopy's sun filter off the direct term once the
      // sun's beam is gone (so the beam is not dimmed by shade it is not subject to).
      {
        const goal = flashWanted ? 1 : 0;
        flashLevel += (goal - flashLevel) * (1 - Math.exp(-dt / FLASH_FADE_S));
        if (Math.abs(goal - flashLevel) < 0.002) flashLevel = goal;
        if (flashLevel > 0 && !flashInScene) { scene.add(flash); scene.add(flash.target); flashInScene = true; }
        else if (flashLevel === 0 && flashInScene) { scene.remove(flash); scene.remove(flash.target); flashInScene = false; }
        if (flashInScene) {
          camera.updateMatrixWorld();
          camera.getWorldDirection(_flashFwd);
          flash.position.set(0.18, -0.14, -0.05).applyMatrix4(camera.matrixWorld);
          flash.target.position.copy(camera.position).addScaledVector(_flashFwd, 6);
          flash.target.updateMatrixWorld();
          flash.intensity = FLASH_INTENSITY * flashLevel;
          if (!perLightShadows) renderer.shadowMap.needsUpdate = true;
        }
        canopyShade.uniforms.uCanopyFlash.value = flashLevel * (1 - THREE.MathUtils.smoothstep(sunBeamLum, 2e-5, 2e-3));
      }

      // Shadow map: redraw when something that casts changed (a new server frame moved the body, a
      // message changed the terrain or roster), when the sun has moved enough to matter, and at
      // least every SHADOW_REFRESH_MS. Otherwise the last map is reused.
      const sunMoved = lastShadowDir.distanceToSquared(_sunDir) > 4e-6;       // about 0.11 degrees
      if (newFrameApplied || shadowDirtyRef.current || sunMoved || nowMs - lastShadowMs > SHADOW_REFRESH_MS) {
        if (perLightShadows) (sunLight.shadow as any).needsUpdate = true; else renderer.shadowMap.needsUpdate = true;
        lastShadowMs = nowMs; lastShadowDir.copy(_sunDir);
        shadowDirtyRef.current = false; newFrameApplied = false; shadowUpdatesWin++;
      }
      canopyShadeRef.current?.update(renderer, dt);     // eases the drawn canopy volume toward the newest one
      renderer.info.reset();                            // so the overlay's counts are this render alone
      renderer.render(scene, camera);

      if (dbgOn && nowMs - statsAt >= 500) updateOverlay(nowMs);
    }
    animate();

    // ── WebGL context loss ───────────────────────────────────────────────────
    const handleContextLost = (e: Event) => {
      e.preventDefault(); // required to allow restoration
      cancelAnimationFrame(animId);
    };
    const handleContextRestored = () => {
      renderer.resetState();
      animate();
    };
    renderer.domElement.addEventListener('webglcontextlost',     handleContextLost);
    renderer.domElement.addEventListener('webglcontextrestored', handleContextRestored);

    // ── Resize ───────────────────────────────────────────────────────────────
    const resizeObs = new ResizeObserver(() => {
      camera.aspect = mount.clientWidth / mount.clientHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(mount.clientWidth, mount.clientHeight);
    });
    resizeObs.observe(mount);

    return () => {
      cancelAnimationFrame(animId);
      resizeObs.disconnect();
      if (document.pointerLockElement === canvas) document.exitPointerLock();
      canvas.removeEventListener('mousedown', onCanvasMouseDown);
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('keydown', onKeyDown);
      document.documentElement.removeAttribute('data-sim-ui-hidden');   // never leave the page hidden behind us
      window.removeEventListener('keyup',   onKeyUp);
      for (const fn of touchUiCleanup) fn();
      renderer.domElement.removeEventListener('webglcontextlost',     handleContextLost);
      renderer.domElement.removeEventListener('webglcontextrestored', handleContextRestored);
      // Clean up all entity groups
      for (const groups of entityGroupsRef.current.values()) {
        for (const g of groups) {
          g.traverse(obj => {
            if (obj instanceof THREE.Mesh) {
              obj.geometry.dispose();
              (obj.material as THREE.Material).dispose();
            }
          });
        }
      }
      entityGroupsRef.current.clear();
      slotToEntityRef.current.clear();
      sceneRef.current = null;
      fixturesRef.current = null;
      if (fruitRef.current) {
        scene.remove(fruitRef.current);
        fruitRef.current.geometry.dispose();
        (fruitRef.current.material as THREE.Material).dispose();
        fruitRef.current = null;
      }
      if (frondRef.current) {
        scene.remove(frondRef.current);
        frondRef.current.geometry.dispose();
        (frondRef.current.material as THREE.Material).dispose();
        frondRef.current = null;
      }
      _frondState.clear();
      fruitLiveRef.current.clear();
      frozenRef.current?.dispose();
      frozenRef.current = null;
      terrain.dispose();
      terrainRef.current = null;
      rocks.dispose();
      rocksRef.current = null;
      grass.dispose();
      grassRef.current = null;
      water.dispose();
      waterRef.current = null;
      canopyShade.dispose();
      canopyShadeRef.current = null;
      windRef.current = null;
      fixGeo.dispose();
      fixMat.dispose();
      siteGeo.dispose();
      groundGeo.dispose();
      groundMat.dispose();
      sunOrbGeo.dispose();
      sunOrbMat.dispose();
      sunGlare.dispose();
      starField.dispose();
      starFieldRef.current = null;
      moonView.dispose();
      horizonGeo.dispose();
      horizonMat.dispose();
      dbg.remove();
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  if (webglUnavailable) {
    return (
      <div className="broadcast-offline">
        <p>WEBGL UNAVAILABLE</p>
        <p className="broadcast-offline-hint">Your browser or GPU does not support WebGL</p>
      </div>
    );
  }

  if (broadcastOffline) {
    return (
      <div className="broadcast-offline">
        <p>SIM OFFLINE</p>
        <p className="broadcast-offline-hint">The simulation tunnel is currently down</p>
        <button onClick={() => { setBroadcastOffline(false); connectBroadcast(); }}>RETRY</button>
      </div>
    );
  }

  return (
    <div ref={mountRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
      {/* Crosshair (only while locked): marks the center pick point for look/select. */}
      {isLocked && (
        <div style={{
          position: 'absolute', top: '50%', left: '50%', width: 6, height: 6,
          transform: 'translate(-50%, -50%)', borderRadius: '50%',
          border: '1px solid rgba(150,210,255,0.8)', pointerEvents: 'none',
        }} />
      )}
      {/* Touch hint (touch devices; hidden at the first touch). */}
      {isTouchDevice && (
        <div ref={touchHintRef} style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.6)', letterSpacing: '0.06em',
        }}>
          {([['WALK', 0, TOUCH_ZONE_LEFT], ['PINCH TO ZOOM', TOUCH_ZONE_LEFT, TOUCH_ZONE_RIGHT], ['TURN', TOUCH_ZONE_RIGHT, 1]] as const).map(([label, a, b]) => (
            <div key={label} style={{ position: 'absolute', bottom: 30, left: `${a * 100}%`, width: `${(b - a) * 100}%`, textAlign: 'center' }}>{label}</div>
          ))}
          <div style={{ position: 'absolute', bottom: 10, left: 10, right: 10, textAlign: 'center', opacity: 0.8 }}>
            ☰ MENU FOR FLASHLIGHT, TILT AND MORE
          </div>
        </div>
      )}
      {/* Landscape only on touch devices: cover the view while in portrait. */}
      {isTouchDevice && isPortrait && (
        <div style={{
          position: 'absolute', inset: 0, zIndex: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(5,7,15,0.94)', textAlign: 'center', padding: 24,
          fontFamily: '"Space Mono", monospace', fontSize: 13,
          color: 'rgba(150,210,255,0.85)', letterSpacing: '0.08em',
        }}>
          ROTATE YOUR DEVICE TO LANDSCAPE
        </div>
      )}
      {/* Control hint (only while unlocked, mouse and keyboard). */}
      {!isLocked && !isTouchDevice && (
        <div style={{
          position: 'absolute', bottom: 10, left: 10, pointerEvents: 'none',
          fontFamily: '"Space Mono", monospace', fontSize: 11,
          color: 'rgba(150,210,255,0.55)', letterSpacing: '0.06em',
        }}>
          CLICK TO LOOK · WASD MOVE · SHIFT SPRINT · H HIDE UI · ESC RELEASE
        </div>
      )}
      <div style={{
        position: 'absolute', top: 8, right: 10, pointerEvents: 'none',
        fontFamily: '"Space Mono", monospace', fontSize: 11,
        color: 'rgba(150,210,255,0.6)', letterSpacing: '0.08em',
      }}>
        <span ref={clockRef} style={{ marginRight: 12 }}>--:--</span>
        TICK <span ref={tickDisplayRef}>—</span>
        <span style={{ marginLeft: 10, opacity: 0.7 }}>TPS <span ref={tickRateRef} style={{ display: 'inline-block', width: '4.5ch', textAlign: 'right' }}>—</span></span>
      </div>
    </div>
  );
}
